import { H3LocalBadge } from "./H3LocalBadge";
import { LtxLocalBadge } from "./LtxLocalBadge";
import { EchoLanguageBadge } from "./EchoLanguageBadge";
import {
  lazy,
  Suspense,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { Link, useLocation } from "react-router";
import {
  CanAccess,
  useGetIdentity,
  useGetList,
  useGetOne,
  useShowContext,
  useStore,
} from "ra-core";
import { Show } from "@/components/admin/show";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertTriangle,
  Activity,
  Bug,
  CheckCircle2,
  Circle,
  Clock3,
  ExternalLink,
  FileJson,
  FileText,
  Film,
  GitBranch,
  Home,
  Image as ImageIcon,
  MapPin,
  Music,
  PauseCircle,
  Plane,
  RefreshCw,
  Smile,
  Star,
  Timer,
  Video,
  Wrench,
  XCircle,
} from "lucide-react";

import type {
  EchoJourneyNode,
  EchoflicksListingIngest,
  EchoflicksSessionJourney,
  EchoflicksSessionCostLineItem,
  EchoflicksSessionEvent,
  EchoflicksSessionFeedback,
  EchoflicksSessionMedia,
  EchoflicksSessionMessage,
  EchoflicksSessionTimings,
  EchoflicksVideoSession,
  I2VPromptAudit,
  I2VPromptAttempt,
  I2VPromptAuditSummary,
} from "../types";
import { EmptyHint, Rating } from "./EchoflicksSessionList";
import {
  INGEST_FIELD_META,
  confidenceLabel,
  diffIngestFieldKeys,
  formatBytes,
  formatDateTime,
  formatMs,
  humanizeAssetRole,
  humanizeMediaKind,
  ingestFieldDisplay,
  ingestMediaBucket,
  ingestOutcomeBadgeVariant,
  ingestRecordsOf,
  isAudio,
  isImage,
  isRealEstateSession,
  isVideo,
  primaryIngestRecord,
  propertyTypeFromRow,
  sessionStatusLabel,
  statusBadgeVariant,
} from "./sessionUtils";
import { ScriptJsonViewer } from "./ScriptJsonViewer";
import { SceneBreakdownPanel } from "./SceneBreakdownPanel";
import { resolveMediaSource } from "./mediaSource";
import { sessionFinalVideoSource, useMediaUrl } from "./useMediaUrl";
import { useDisplayableImage } from "./useDisplayableImage";
import {
  WhatsAppChat,
  WhatsAppChatHeader,
  buildConversationTimeline,
  normalizeSessionMessage,
  whatsAppHistoryRows,
} from "../whatsapp/chat";
import { Switch } from "@/components/ui/switch";
import {
  EchoBrief,
  EchoDirectorDecisions,
  EchoHeader,
  EchoI2VBeforeAfter,
  EchoQcLayers,
  EchoQualityChecks,
  EchoStoryboard,
  EchoTitleSection,
} from "./EchoStoryView";
import { I2VDirectorSummary } from "./I2VDirectorSummary";
import { I2VFallbackChain } from "./I2VFallbackChain";
import { ParallaxPreviewSection } from "./ParallaxPreviewSection";
import { FinalVideoDeliverySummary } from "./FinalVideoDeliverySummary";
import { RepromptEvidenceSummary } from "./RepromptEvidenceSummary";
import { mergeDefinedSummary } from "./i2vDiagnostics";
import { EchoSourceChip } from "./EchoAttributionBadges";
import { TestEchoBadge } from "./TestEchoBadge";
import { WebEchoBadge } from "./WebEchoBadge";
import { isWebEcho } from "./webEcho";
import { useScriptEnvelope } from "./useScriptEnvelope";
import { RemakesPreviewCard } from "./RemakesPreviewCard";
import { RemakeWatchView } from "./RemakeWatchView";
import { FramingDiagnosticsCard } from "./FramingDiagnosticsCard";
import { FramingSection } from "./FramingSection/FramingSection";
import { IdentitySection } from "./IdentitySection/IdentitySection";
import { DogCensusCard } from "./DogCensusCard";
import { DogCastSection } from "./DogCastSection";
import { BuildingProfileCard } from "./BuildingProfileCard";
import { FloorPlanCard } from "./FloorPlanCard";
import { RoomMapCard } from "./RoomMapCard";
import { sessionEventDetail, sessionEventLabel } from "./soundtrack";
import { SpatialEvidenceCard } from "./SpatialEvidence/SpatialEvidenceCard";
import { getDefaultAwsMediaBucket } from "../providers/aws";
import type { SessionCostsMeta } from "../providers/aws/dataProvider";
import { IdentityWorkbenchDialog } from "./IdentityWorkbench/IdentityWorkbenchDialog";
import {
  INITIAL_IDENTITY_WORKBENCH_STATE,
  identityWorkbenchReducer,
  parseIdentityDeepLinkHints,
} from "./IdentityWorkbench/identityWorkbenchState";
import { FramingWorkbenchDialog } from "./FramingWorkbench/FramingWorkbenchDialog";
import {
  INITIAL_WORKBENCH_STATE,
  framingWorkbenchReducer,
  parseFramingDeepLinkHints,
} from "./FramingWorkbench/framingWorkbenchState";
import { DisplayCropBadge } from "./DisplayCropBadge";
import { EchoCostSection } from "./EchoCostSection";
import { DerivativeEchoBanner } from "./DerivativeEchoBanner";
import { hasDerivativeRenders } from "./renderCosts";
import { EchoVoiceFidelity } from "./EchoVoiceFidelity";
import { EchoVoiceSelection } from "./EchoVoiceSelection";
import { EchoAccentComparison } from "./EchoAccentComparison";
import { detectedMusicBpm } from "./scriptStory";
import { ContinuityAuditCard } from "./ContinuityAuditCard";
import { PetAdaptationCard } from "./PetAdaptationCard";
import {
  deriveJourneyI2V,
  mergeSceneI2V,
  useEchoStateManifest,
} from "./echoStateManifest";
import { parseFeedbackTimestampMs } from "../feedback/feedbackDeepLink";
import { DurableEchoLedgerCard } from "./DurableEchoLedgerCard";
import { EchoDirectorView } from "./EchoDirectorView";
import { NarrationContextBadge } from "./NarrationContextBadge";
import { readStoryFidelity, scriptLoadStatus } from "./storyFidelity";
import { EchoNarrationAb } from "./NarrationAbPanel";
import { useDirectorViewIdentityReport } from "./useDirectorViewIdentityReport";
import { ImagePreprocessingSection } from "./ImagePreprocessing/ImagePreprocessingSection";
import { CreativeLineageCard } from "./CreativeLineageCard";
import { StoryboardFirstAuditCard } from "./StoryboardFirstAuditCard";
import { SceneFlagsCard } from "./SceneFlagsCard";
import { FilmChangeRequestsCard } from "./FilmChangeRequests/FilmChangeRequestsCard";
import { editChannelBadge } from "./journey/editChannel";
import { EditChannelChip } from "./journey/EditChannelChip";

// EF-EJ-3: the cinematic React Flow canvas ("Echo Atelier") ships behind a
// build-time flag. Flag off keeps the legacy EchoJourneyPanel below. Lazy so
// React Flow (and its CSS) is only fetched when the flag is enabled.
const JOURNEY_V2_ENABLED =
  import.meta.env.VITE_ECHOFLICKS_JOURNEY_V2 === "true";
const EchoAtelier = lazy(() => import("./journey/EchoAtelier"));
const EchoFelixLauncher = lazy(
  () => import("../echo-explainer/EchoFelixLauncher"),
);
// The person cast panel (ranking, sheet budget, mouth locks, SAM 3 isolation)
// is review tooling most viewers scroll past; lazy so it stays out of the
// entry chunk (scripts/bundle-budget.json).
const PersonCastSection = lazy(() =>
  import("./PersonCastSection").then((module) => ({
    default: module.PersonCastSection,
  })),
);
// MYC-090: only web Echoes (my.echoflicks.com) render the Web transcript, so
// WhatsApp Echo pages never download it.
const WebTranscriptPanel = lazy(() =>
  import("./WebTranscriptPanel").then((module) => ({
    default: module.WebTranscriptPanel,
  })),
);
// MB-051: the media bin card fetches only near the viewport; lazy so its code
// stays out of the entry chunk too.
const MediaBinCard = lazy(() =>
  import("./MediaBin/MediaBinCard").then((module) => ({
    default: module.MediaBinCard,
  })),
);
// ECL-062: only an Echo with edits or remakes (its summary's
// echo_cost_derivatives_count) downloads the render costs card.
// EMC-072/073/076: loaded on demand to keep the Echo page entry chunk small;
// the marketing card and the reconciliation panel are only fetched for
// administrators.
const EchoCostReconciliationPanel = lazy(() =>
  import("./EchoCostReconciliationPanel").then((module) => ({
    default: module.EchoCostReconciliationPanel,
  })),
);
const EchoWhatsAppCostCard = lazy(() =>
  import("./EchoWhatsAppCostCard").then((module) => ({
    default: module.EchoWhatsAppCostCard,
  })),
);
const EchoMarketingCostCard = lazy(() =>
  import("./EchoMarketingCostCard").then((module) => ({
    default: module.EchoMarketingCostCard,
  })),
);
const EchoRenderCostsCard = lazy(() =>
  import("./EchoRenderCostsCard").then((module) => ({
    default: module.EchoRenderCostsCard,
  })),
);

// ECP-003: the timing waterfall (scene and LLM lanes, critical path) is
// staff telemetry below the fold; lazy so it stays out of the entry chunk.
const TimingWaterfallPanel = lazy(() =>
  import("./TimingWaterfallPanel").then((module) => ({
    default: module.TimingWaterfallPanel,
  })),
);

export const EchoflicksSessionShow = () => (
  <Show actions={false}>
    <SessionShowContent />
  </Show>
);

const SessionShowContent = () => {
  const { record } = useShowContext<EchoflicksVideoSession>();
  const location = useLocation();
  const remakeId = new URLSearchParams(location.search).get("remake");
  if (remakeId !== null) {
    return record ? (
      <RemakeWatchView record={record} remakeId={remakeId} />
    ) : null;
  }
  return <SessionDetailsContent />;
};

const SessionDetailsContent = () => {
  const { record } = useShowContext<EchoflicksVideoSession>();
  const { identity } = useGetIdentity();
  const location = useLocation();
  const related = useSessionRelatedData(record?.id);
  const finalVideoMedia =
    related.media.find((media) => media.kind === "final_video") ?? null;
  const finalVideoSource = record
    ? resolveMediaSource(sessionFinalVideoSource(record), finalVideoMedia)
    : null;
  const finalVideoUrl = useMediaUrl(finalVideoSource);
  const [debug, setDebug] = useStore<boolean>(
    "echoflicks.echoShow.debug",
    false,
  );
  const feedbackDeepLink = useMemo(() => {
    const search = new URLSearchParams(location.search);
    return {
      feedbackId: search.get("feedbackId"),
      timestampMs: parseFeedbackTimestampMs(search),
      sceneId: search.get("framingScene"),
    };
  }, [location.search]);
  // Untrusted navigation hints; validated against the authorized inspector
  // response before the workbench treats them as exact (CROP-WB-006).
  const framingHints = useMemo(
    () => parseFramingDeepLinkHints(location.search),
    [location.search],
  );
  const identityHints = useMemo(
    () => parseIdentityDeepLinkHints(location.search),
    [location.search],
  );
  const fromIdentityWorkbench = useMemo(
    () => new URLSearchParams(location.search).get("identityEvidence") === "1",
    [location.search],
  );
  const [workbenchState, workbenchDispatch] = useReducer(
    framingWorkbenchReducer,
    INITIAL_WORKBENCH_STATE,
  );
  const [identityWorkbenchState, identityWorkbenchDispatch] = useReducer(
    identityWorkbenchReducer,
    INITIAL_IDENTITY_WORKBENCH_STATE,
  );
  const workbenchAutoOpenedRef = useRef(false);
  const identityWorkbenchAutoOpenedRef = useRef(false);
  useEffect(() => {
    if (
      identity?.administrator &&
      (feedbackDeepLink.feedbackId || feedbackDeepLink.sceneId)
    ) {
      setDebug(true);
    }
  }, [feedbackDeepLink, identity?.administrator, setDebug]);
  // An already-authorized feedback deep link opens the requested inspector
  // directly (invariant 1); it never auto-opens for non-admin viewers.
  useEffect(() => {
    if (
      !identity?.administrator ||
      workbenchAutoOpenedRef.current ||
      !framingHints.sceneId ||
      (!framingHints.feedbackId && !fromIdentityWorkbench)
    ) {
      return;
    }
    workbenchAutoOpenedRef.current = true;
    workbenchDispatch({
      type: "open",
      sceneId: framingHints.sceneId,
      attemptId: framingHints.attemptId,
      stage: framingHints.stage ?? "encoded",
      timeSeconds:
        framingHints.timestampMs != null
          ? framingHints.timestampMs / 1000
          : null,
    });
  }, [framingHints, fromIdentityWorkbench, identity?.administrator]);
  useEffect(() => {
    if (
      !identity?.administrator ||
      identityWorkbenchAutoOpenedRef.current ||
      !identityHints.identityId
    ) {
      return;
    }
    identityWorkbenchAutoOpenedRef.current = true;
    identityWorkbenchDispatch({
      type: "open",
      identityId: identityHints.identityId,
      manifestVersion: identityHints.manifestVersion,
    });
    if (identityHints.panel) {
      identityWorkbenchDispatch({
        type: "set_panel",
        panel: identityHints.panel,
      });
    }
    if (identityHints.instanceId) {
      identityWorkbenchDispatch({
        type: "select_instance",
        instanceId: identityHints.instanceId,
      });
    }
    if (identityHints.timeUs != null) {
      identityWorkbenchDispatch({
        type: "set_time",
        timeUs: identityHints.timeUs,
      });
    }
  }, [identityHints, identity?.administrator]);
  useEffect(() => {
    if (!debug || related.journeyLoading || !feedbackDeepLink.sceneId) return;
    const frame = requestAnimationFrame(() => {
      document
        .getElementById(`framing-scene-${feedbackDeepLink.sceneId}`)
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
    return () => cancelAnimationFrame(frame);
  }, [debug, feedbackDeepLink.sceneId, related.journeyLoading]);
  const { state: scriptState, scriptUrlState } = useScriptEnvelope(
    record,
    related.media,
  );
  const manifestState = useEchoStateManifest(record, related.media);
  // CSF-034: which narration context this Echo used, from the script JSON.
  // Media still loading may yet reveal the script, so it counts as loading.
  const storyFidelity = useMemo(
    () =>
      readStoryFidelity(
        scriptState.status === "loaded" ? scriptState.envelope : undefined,
        scriptLoadStatus({
          status: scriptState.status,
          loading: scriptUrlState.isLoading || related.mediaLoading,
          urlError: scriptUrlState.status === "error",
        }),
      ),
    [
      scriptState,
      scriptUrlState.isLoading,
      scriptUrlState.status,
      related.mediaLoading,
    ],
  );
  const directorViewIdentity = useDirectorViewIdentityReport({
    sessionId: record?.id == null ? "" : String(record.id),
    summary: record?.identity_summary,
    availabilityHint: record?.identity_availability,
    manifestStatusHint: record?.identity_manifest_status,
    enabled: Boolean(record && identity?.administrator),
  });
  // Effective I2V prompts: manifest (full text) wins, journey nodes fill gaps.
  const sceneI2VByScene = useMemo(
    () =>
      mergeSceneI2V(manifestState.byScene, deriveJourneyI2V(related.journey)),
    [manifestState.byScene, related.journey],
  );
  // MYC-090: made on my.echoflicks.com (no WhatsApp). The ECHO row marker, or
  // the session's web events / journey nodes before the indexer stamps it.
  const webEcho = useMemo(
    () =>
      isWebEcho({
        record,
        events: related.events,
        journeyNodes: related.journey?.nodes,
      }),
    [record, related.events, related.journey?.nodes],
  );
  // D6: allocated marketing for administrators only, and never when the API
  // says it removed it for this caller (contract C6 `acquisition_visible`).
  const showMarketing =
    Boolean(identity?.administrator) &&
    related.costsAcquisitionVisible !== false;
  const whatsAppMessages = useMemo(
    () => whatsAppHistoryRows(related.messages, { webEcho }),
    [related.messages, webEcho],
  );

  if (!record) return null;

  const uploadedMedia = related.media.filter(
    (media) => media.kind === "uploaded_media",
  );
  const generatedAssets = related.media.filter((media) =>
    isGeneratedSceneAsset(media),
  );
  // Real-estate echoes get a distinct layout: the map clip and the aerial drone shot
  // are surfaced as their own playable sections (see RealEstateAssetSections), so the
  // generic "Generated Assets" sidebar card is hidden to avoid showing them twice.
  const isRealEstate = isRealEstateSession(record, related.media);

  const envelope =
    scriptState.status === "loaded" ? scriptState.envelope : undefined;
  const scriptLoading =
    scriptState.status === "loading" || scriptUrlState.isLoading;
  const musicBpm = detectedMusicBpm(related.events);

  return (
    // Full width: Layout drops its content cap on this route so the Director
    // View stage (player + original side by side) can use the whole screen.
    <div className="space-y-4">
      <div className="flex">
        <NarrationContextBadge fidelity={storyFidelity} />
      </div>

      <EchoHeader
        record={record}
        finalVideo={finalVideoUrl}
        isWebEcho={webEcho}
        isAdmin={Boolean(identity?.administrator)}
      >
        {record.workload_id ? (
          <CanAccess resource="echo_studio" action="list">
            <Button asChild variant="outline" size="sm">
              <Link
                to={`/echo-studio/${encodeURIComponent(record.workload_id)}`}
              >
                <Film className="size-4" />
                Echo Studio replay
              </Link>
            </Button>
          </CanAccess>
        ) : null}
        {identity?.administrator ? (
          <label className="flex cursor-pointer select-none items-center gap-2 text-sm text-muted-foreground">
            <Bug className="size-4" />
            Debug
            <Switch
              checked={debug}
              onCheckedChange={setDebug}
              aria-label="Show engineering and telemetry details"
            />
          </label>
        ) : null}
      </EchoHeader>

      <EchoDirectorView
        record={record}
        envelope={envelope}
        media={related.media}
        events={related.events}
        manifestBody={manifestState.body}
        sceneI2VByScene={sceneI2VByScene}
        feedbackContext={{
          envelope,
          media: related.media,
          journey: related.journey,
          events: related.events,
          sceneI2VByScene,
        }}
        feedbackTimestampMs={feedbackDeepLink.timestampMs}
        finalVideoSource={finalVideoSource}
        isAdmin={Boolean(identity?.administrator)}
        identityReport={directorViewIdentity.report}
        identityMode={directorViewIdentity.mode}
        loading={scriptLoading}
        storyFidelity={storyFidelity}
      />

      {isRealEstate ? <RealEstateAssetSections media={related.media} /> : null}

      <EchoBrief
        record={record}
        media={related.media}
        messages={related.messages}
      />

      <IdentitySection
        summary={record.identity_summary}
        availabilityHint={record.identity_availability}
        manifestStatusHint={record.identity_manifest_status}
        isAdmin={Boolean(identity?.administrator)}
        onInspectIdentity={(identityId, manifestVersion) =>
          identityWorkbenchDispatch({
            type: "open",
            identityId,
            manifestVersion,
          })
        }
      />

      <DogCensusCard session={record} />

      <DogCastSection
        events={related.events}
        media={related.media}
        isAdmin={Boolean(identity?.administrator)}
      />

      <Suspense fallback={null}>
        <PersonCastSection
          events={related.events}
          media={related.media}
          isAdmin={Boolean(identity?.administrator)}
          envelope={envelope}
        />
      </Suspense>

      <PetAdaptationCard events={related.events} />

      <ImagePreprocessingSection
        sessionId={record.id}
        media={uploadedMedia}
        canViewTechnical={Boolean(identity?.administrator)}
      />

      <EchoTitleSection
        envelope={envelope}
        media={related.media}
        loading={scriptLoading}
      />

      <EchoDirectorDecisions
        record={record}
        envelope={envelope}
        musicBpm={musicBpm}
        loading={scriptLoading}
      />

      <EchoVoiceSelection
        envelope={envelope}
        isNarrated={record.is_narrated}
        contentLanguage={record.content_language}
        loading={scriptLoading}
      />

      <EchoVoiceFidelity voiceRender={record.voice_render} />

      <EchoAccentComparison
        comparison={record.accent_comparison}
        voiceRender={record.voice_render}
        envelope={envelope}
      />

      <EchoNarrationAb
        envelope={envelope}
        isAdmin={Boolean(identity?.administrator)}
        loading={scriptLoading}
      />

      <CreativeLineageCard lineage={record.creative_lineage} />

      <EchoStoryboard
        envelope={envelope}
        media={related.media}
        sceneI2VByScene={sceneI2VByScene}
        loading={scriptLoading}
      />

      <EchoI2VBeforeAfter
        envelope={envelope}
        media={related.media}
        sceneI2VByScene={sceneI2VByScene}
        loading={scriptLoading}
      />

      <ParallaxPreviewSection events={related.events} media={related.media} />

      <I2VFallbackChain
        sceneI2VByScene={sceneI2VByScene}
        loading={manifestState.status === "loading"}
      />

      <I2VDirectorSummary
        journey={related.journey}
        isLoading={related.journeyLoading}
        showTechnical={Boolean(identity?.administrator)}
        envelope={envelope}
        media={related.media}
      />

      {identity?.administrator ? (
        <IdentityWorkbenchDialog
          sessionId={String(record.id)}
          state={identityWorkbenchState}
          dispatch={identityWorkbenchDispatch}
          hints={identityHints}
          onOpenFraming={(sceneId) => {
            identityWorkbenchDispatch({ type: "close" });
            workbenchDispatch({ type: "open", sceneId });
          }}
        />
      ) : null}

      <FramingSection
        sessionId={record.id}
        journey={related.journey}
        feedback={related.feedback}
        isAdmin={Boolean(identity?.administrator)}
        onInspectScene={(sceneId) =>
          workbenchDispatch({
            type: "open",
            sceneId,
            attemptId: framingHints.attemptId,
            stage: framingHints.stage,
            timeSeconds:
              framingHints.timestampMs != null
                ? framingHints.timestampMs / 1000
                : null,
          })
        }
      />
      {identity?.administrator ? (
        <FramingWorkbenchDialog
          sessionId={record.id}
          state={workbenchState}
          dispatch={workbenchDispatch}
          hints={framingHints}
        />
      ) : null}

      <FinalVideoDeliverySummary journey={related.journey} />
      <RepromptEvidenceSummary journey={related.journey} />

      <EchoQualityChecks session={record} />

      {/* ECL-062: a child Echo's cost is part of its parent's renders. */}
      <DerivativeEchoBanner session={record} />
      <EchoCostSection
        session={record}
        costs={related.costs}
        isLoading={related.costsLoading}
        showMarketing={showMarketing}
      />
      {/* EMC-072: Meta's per-message fees, for every operator. */}
      <Suspense fallback={null}>
        <EchoWhatsAppCostCard
          session={record}
          costs={related.costs}
          isLoading={related.costsLoading}
        />
      </Suspense>
      {/* EMC-073: allocated acquisition spend, admins only (D6). */}
      {showMarketing ? (
        <Suspense fallback={null}>
          <EchoMarketingCostCard
            session={record}
            costs={related.costs}
            isAdmin
            isLoading={related.costsLoading}
          />
        </Suspense>
      ) : null}
      {hasDerivativeRenders(record) ? (
        <Suspense fallback={null}>
          <EchoRenderCostsCard session={record} />
        </Suspense>
      ) : null}
      {identity?.administrator ? (
        <Suspense fallback={null}>
          <EchoCostReconciliationPanel sessionId={record.id} isAdmin />
        </Suspense>
      ) : null}

      {/* MYC-090: a web Echo's interview lives in its web timeline. Fetched
          only for a web Echo; the WhatsApp card below never shows web turns. */}
      {webEcho ? (
        <Suspense fallback={null}>
          <WebTranscriptPanel workloadId={record.workload_id} enabled />
        </Suspense>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>Conversation</CardTitle>
          <p className="text-sm text-muted-foreground">
            {webEcho ? (
              <>
                The WhatsApp messages for this echo. It was made on
                my.echoflicks.com, so the Felix interview is in the Web
                transcript above, not here.
              </>
            ) : (
              <>
                The WhatsApp messages for this echo, from{" "}
                <span className="font-mono">/create</span> through delivery.
                Ticks show Meta&apos;s delivery status; the chip beside them is
                what Meta charged for the message.
              </>
            )}
          </p>
        </CardHeader>
        <CardContent>
          {whatsAppMessages.length > 0 ? (
            <MessageTimeline
              messages={whatsAppMessages}
              media={related.media}
              contactName={record.contact_name}
              whatsappId={record.whatsapp_user_id}
            />
          ) : (
            <EmptyHint>
              {webEcho
                ? "No WhatsApp messages: this echo was made on my.echoflicks.com."
                : "No chat transcript was available for this echo."}
            </EmptyHint>
          )}
        </CardContent>
      </Card>

      <FeedbackCard record={record} feedback={related.feedback} />

      {/* WRP-062: what the customer asked the team to change on
          my.echoflicks.com ("Ask us to change it"). Hidden when none. */}
      <FilmChangeRequestsCard sessionId={record.id} />

      {/* MB-051: every original upload (used or not) with its handle, next to
          the Remakes card an operator re-cuts from. Read-only. */}
      <Suspense fallback={null}>
        <MediaBinCard sessionId={record.id} />
      </Suspense>

      {/* Admin-only: the card self-gates on identity.administrator, owns its own
          remakes fetch (admin-gated + polled while any attempt is in flight),
          and renders nothing for non-admins. */}
      <RemakesPreviewCard record={record} />
      {identity?.administrator ? (
        <Suspense fallback={null}>
          <EchoFelixLauncher
            key={String(record.id)}
            echoId={String(record.id)}
          />
        </Suspense>
      ) : null}

      {debug && identity?.administrator ? (
        <DebugSection
          record={record}
          related={related}
          isRealEstate={isRealEstate}
          uploadedMedia={uploadedMedia}
          generatedAssets={generatedAssets}
          canViewTechnical={Boolean(identity?.administrator)}
          webEcho={webEcho}
        />
      ) : null}
    </div>
  );
};

const FeedbackCard = ({
  record,
  feedback,
}: {
  record: EchoflicksVideoSession;
  feedback: EchoflicksSessionFeedback[];
}) => (
  <Card>
    <CardHeader>
      <CardTitle>Rating &amp; feedback</CardTitle>
    </CardHeader>
    <CardContent>
      {feedback.length > 0 || record.rating ? (
        <div className="space-y-3">
          <Rating rating={record.rating} />
          {feedback.map((item) => (
            <div key={item.id} className="text-sm">
              <div className="text-muted-foreground">
                {formatDateTime(item.occurred_at)}
              </div>
              {item.feedback_text ? (
                <p className="mt-1">{item.feedback_text}</p>
              ) : null}
            </div>
          ))}
        </div>
      ) : (
        <EmptyHint>No rating or feedback yet for this echo.</EmptyHint>
      )}
    </CardContent>
  </Card>
);

const DebugSection = ({
  record,
  related,
  isRealEstate,
  uploadedMedia,
  generatedAssets,
  canViewTechnical,
  webEcho,
}: {
  record: EchoflicksVideoSession;
  related: ReturnType<typeof useSessionRelatedData>;
  isRealEstate: boolean;
  uploadedMedia: EchoflicksSessionMedia[];
  generatedAssets: EchoflicksSessionMedia[];
  canViewTechnical: boolean;
  webEcho: boolean;
}) => (
  <div className="space-y-4">
    <div className="flex items-center gap-2 pt-2">
      <Wrench className="size-4 text-muted-foreground" />
      <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        Engineering &amp; telemetry
      </span>
      <span className="h-px flex-1 bg-border" />
    </div>

    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle>
              {record.movie_title ?? record.script_title ?? "Video session"}
            </CardTitle>
            <div className="mt-2 flex flex-wrap gap-2">
              <Badge variant={statusBadgeVariant(record.status)}>
                {sessionStatusLabel(record.status)}
              </Badge>
              <Badge variant="outline">
                {confidenceLabel(record.source_confidence)}
              </Badge>
              {record.rating ? (
                <Badge variant="secondary">
                  <Star className="size-3 fill-current" />
                  {record.rating}/5
                </Badge>
              ) : null}
              {isRealEstate ? (
                <Badge variant="outline">
                  <Home className="size-3" />
                  Real Estate
                </Badge>
              ) : null}
              <H3LocalBadge summary={record.i2v_provider_summary} />
              <LtxLocalBadge summary={record.i2v_provider_summary} />
              <EchoSourceChip record={record} />
              <WebEchoBadge record={record} isWeb={webEcho} />
              <TestEchoBadge record={record} />
              <EchoLanguageBadge record={record} />
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {record.contact_id ? (
              <Button asChild variant="outline" size="sm">
                <Link to={`/contacts/${record.contact_id}/show`}>
                  Contact
                  <ExternalLink className="size-4" />
                </Link>
              </Button>
            ) : null}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <dl className="grid gap-4 text-sm sm:grid-cols-2 lg:grid-cols-4">
          <MetaItem label="WhatsApp ID" value={record.whatsapp_user_id} />
          <MetaItem label="Session ID" value={record.session_id} />
          <MetaItem label="Workload ID" value={record.workload_id} />
          <MetaItem label="Started" value={formatDateTime(record.started_at)} />
          <MetaItem
            label="Completed"
            value={formatDateTime(record.completed_at)}
          />
          <MetaItem
            label="Media"
            value={`${record.uploaded_media_count ?? 0} uploaded / ${
              record.media_count ?? related.media.length
            } total`}
          />
        </dl>
      </CardContent>
    </Card>

    <DurableEchoLedgerCard workloadId={record.workload_id} />

    {JOURNEY_V2_ENABLED ? (
      <Suspense
        fallback={
          <div className="flex h-[520px] items-center justify-center rounded-xl border bg-muted/10 text-sm text-muted-foreground">
            Loading journey…
          </div>
        }
      >
        <EchoAtelier
          journey={related.journey}
          isLoading={related.journeyLoading}
        />
      </Suspense>
    ) : (
      <EchoJourneyPanel
        journey={related.journey}
        isLoading={related.journeyLoading}
        webEcho={webEcho}
      />
    )}

    <I2VPromptDiagnosticsCard
      journey={related.journey}
      media={related.media}
      isLoading={related.journeyLoading}
    />

    <StoryboardFirstAuditCard
      journey={related.journey}
      isLoading={related.journeyLoading}
    />

    <ContinuityAuditCard events={related.events} />

    {canViewTechnical ? (
      <FramingDiagnosticsCard
        journey={related.journey}
        media={related.media}
        feedback={related.feedback}
        isLoading={related.journeyLoading}
        sessionId={record.id}
        canViewTechnical
      />
    ) : null}

    <ExpressionQCPanel
      journey={related.journey}
      isLoading={related.journeyLoading}
    />

    <EchoQcLayers session={record} />

    {/* C2: staff scene flags beside the QC verdict (self-gates on administrator). */}
    <SceneFlagsCard session={record} />

    <JourneyDebugPanel
      journey={related.journey}
      isLoading={related.journeyLoading}
    />

    <Suspense fallback={null}>
      <TimingWaterfallPanel
        timings={related.timings}
        isLoading={related.timingsLoading}
      />
    </Suspense>

    <SceneBreakdownPanel session={record} media={related.media} />

    <ListingIngestCard
      media={related.media}
      session={record}
      events={related.events}
    />

    <BuildingProfileCard events={related.events} media={related.media} />
    <RoomMapCard events={related.events} />
    {canViewTechnical ? (
      <SpatialEvidenceCard
        key={String(record.id)}
        sessionId={String(record.id)}
        workloadId={record.workload_id}
      />
    ) : null}

    <ScriptJsonViewer session={record} media={related.media} />

    <Card>
      <CardHeader>
        <CardTitle>Submitted Media</CardTitle>
      </CardHeader>
      <CardContent>
        {uploadedMedia.length > 0 ? (
          <MediaGrid media={uploadedMedia} />
        ) : (
          <EmptyHint>No submitted media is linked to this session.</EmptyHint>
        )}
      </CardContent>
    </Card>

    {!isRealEstate ? (
      <Card>
        <CardHeader>
          <CardTitle>Generated Assets</CardTitle>
        </CardHeader>
        <CardContent>
          {generatedAssets.length > 0 ? (
            <AssetList media={generatedAssets} />
          ) : (
            <EmptyHint>No generated assets were linked.</EmptyHint>
          )}
        </CardContent>
      </Card>
    ) : null}

    <Card>
      <CardHeader>
        <CardTitle>Events</CardTitle>
      </CardHeader>
      <CardContent>
        {related.events.length > 0 ? (
          <EventList events={related.events} />
        ) : (
          <EmptyHint>No backend events were linked.</EmptyHint>
        )}
      </CardContent>
    </Card>

    <Card>
      <CardHeader>
        <CardTitle>Raw metadata</CardTitle>
      </CardHeader>
      <CardContent>
        <pre className="max-h-72 overflow-auto rounded-md bg-muted p-3 text-xs">
          {JSON.stringify(record.raw_metadata ?? {}, null, 2)}
        </pre>
      </CardContent>
    </Card>
  </div>
);

const useSessionRelatedData = (sessionId?: string | number) => {
  const enabled = sessionId != null;
  const commonParams = {
    filter: { session_id: sessionId },
    // The dashboard API paginates internally. Keep the complete Echo relation in
    // React Admin so late reports/receipts are not hidden behind the old 100-row
    // UI cap.
    pagination: { page: 1, perPage: 1000 },
  };

  const media = useGetList<EchoflicksSessionMedia>(
    "echoflicks_session_media",
    {
      ...commonParams,
      sort: { field: "created_at_source", order: "ASC" },
    },
    { enabled },
  );
  const messages = useGetList<EchoflicksSessionMessage>(
    "echoflicks_session_messages",
    {
      ...commonParams,
      sort: { field: "occurred_at", order: "ASC" },
    },
    { enabled },
  );
  const feedback = useGetList<EchoflicksSessionFeedback>(
    "echoflicks_session_feedback",
    {
      ...commonParams,
      sort: { field: "occurred_at", order: "DESC" },
    },
    { enabled },
  );
  const events = useGetList<EchoflicksSessionEvent>(
    "echoflicks_session_events",
    {
      ...commonParams,
      sort: { field: "occurred_at", order: "ASC" },
    },
    { enabled },
  );
  const costs = useGetList<EchoflicksSessionCostLineItem>(
    "echoflicks_session_costs",
    {
      ...commonParams,
      sort: { field: "occurred_at", order: "ASC" },
    },
    { enabled },
  );
  const journey = useGetOne<EchoflicksSessionJourney>(
    "echoflicks_session_journeys",
    { id: sessionId ?? "" },
    { enabled },
  );
  const timings = useGetOne<EchoflicksSessionTimings>(
    "echoflicks_session_timings",
    { id: sessionId ?? "" },
    { enabled },
  );

  return {
    media: media.data ?? [],
    mediaLoading: media.isLoading,
    messages: messages.data ?? [],
    feedback: feedback.data ?? [],
    events: events.data ?? [],
    costs: costs.data ?? [],
    costsLoading: costs.isLoading,
    // EMC (contract C6): false when the API removed allocated marketing for
    // this caller; null from an API that predates EMC.
    costsAcquisitionVisible:
      (costs.meta as SessionCostsMeta | undefined)?.acquisitionVisible ?? null,
    journey: journey.data,
    journeyLoading: journey.isLoading,
    timings: timings.data,
    timingsLoading: timings.isLoading,
  };
};

const journeyStages = [
  "intake",
  "conversation",
  "director",
  "script",
  "composition",
  "delivery",
  "feedback",
];

const EchoJourneyPanel = ({
  journey,
  isLoading,
  webEcho = false,
}: {
  journey?: EchoflicksSessionJourney;
  isLoading?: boolean;
  webEcho?: boolean;
}) => {
  const [selectedNode, setSelectedNode] = useState<EchoJourneyNode | null>(
    null,
  );
  const nodesByStage = useMemo(() => {
    const groups = new Map<string, EchoJourneyNode[]>();
    for (const node of journey?.nodes ?? []) {
      const stage = node.stage || "conversation";
      const group = groups.get(stage) ?? [];
      group.push(node);
      groups.set(stage, group);
    }
    for (const group of groups.values()) {
      group.sort((left, right) =>
        String(left.occurred_at ?? left.id).localeCompare(
          String(right.occurred_at ?? right.id),
        ),
      );
    }
    return groups;
  }, [journey?.nodes]);

  const orderedStages = useMemo(() => {
    const seen = new Set(journeyStages);
    const extraStages = [...nodesByStage.keys()].filter(
      (stage) => !seen.has(stage),
    );
    return [...journeyStages, ...extraStages];
  }, [nodesByStage]);

  const nodes = journey?.nodes ?? [];
  const summary = journey?.summary;

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <CardTitle>Echo Journey</CardTitle>
          {summary || webEcho ? (
            <div className="flex flex-wrap gap-2">
              <WebEchoBadge isWeb={webEcho} />
              {summary ? (
                <>
                  <Badge
                    variant={
                      summary.status === "failed" ? "destructive" : "outline"
                    }
                  >
                    {summary.status ?? "unknown"}
                  </Badge>
                  {summary.fallback_count ? (
                    <Badge variant="secondary">
                      {summary.fallback_count} fallback
                    </Badge>
                  ) : null}
                  {summary.confidence ? (
                    <Badge variant="outline">{summary.confidence}</Badge>
                  ) : null}
                </>
              ) : null}
            </div>
          ) : null}
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <EmptyHint>Loading journey...</EmptyHint>
        ) : nodes.length > 0 ? (
          <div className="overflow-x-auto pb-2">
            <div className="grid min-w-[760px] grid-cols-7 gap-3">
              {orderedStages.map((stage) => (
                <div key={stage} className="space-y-2">
                  <div className="text-xs font-medium uppercase text-muted-foreground">
                    {stage.replaceAll("_", " ")}
                  </div>
                  <div className="space-y-2">
                    {(nodesByStage.get(stage) ?? []).map((node) => (
                      <button
                        key={node.id}
                        type="button"
                        title={`${node.label} / ${node.status}`}
                        onClick={() => setSelectedNode(node)}
                        className={`flex min-h-16 w-full items-start gap-2 rounded-md border p-2 text-left text-sm transition hover:border-primary ${journeyNodeClass(
                          node.status,
                        )}`}
                      >
                        <JourneyStatusIcon status={node.status} />
                        <span className="min-w-0">
                          <span className="block truncate font-medium">
                            {node.label}
                          </span>
                          <span className="block truncate text-xs text-muted-foreground">
                            {node.service ?? node.type}
                          </span>
                          <EditChannelChip
                            badge={editChannelBadge(node)}
                            className="mt-1"
                          />
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <EmptyHint>
            No journey nodes are available for this session yet.
          </EmptyHint>
        )}
      </CardContent>
      <JourneyNodeDialog
        node={selectedNode}
        onOpenChange={(open) => {
          if (!open) setSelectedNode(null);
        }}
      />
    </Card>
  );
};

type I2VPromptAuditEntry = {
  key: string;
  summary: I2VPromptAuditSummary;
  audit: I2VPromptAudit | null;
  node: EchoJourneyNode | null;
  media: EchoflicksSessionMedia | null;
  attempt: I2VPromptAttempt | null;
};

const I2VPromptDiagnosticsCard = ({
  journey,
  media,
  isLoading,
}: {
  journey?: EchoflicksSessionJourney;
  media: EchoflicksSessionMedia[];
  isLoading?: boolean;
}) => {
  const [selected, setSelected] = useState<I2VPromptAuditEntry | null>(null);
  const entries = useMemo(
    () => collectI2VPromptAuditEntries(journey, media),
    [journey, media],
  );

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>I2V Prompt Diagnostics</CardTitle>
        </CardHeader>
        <CardContent>
          <EmptyHint>Loading prompt audits...</EmptyHint>
        </CardContent>
      </Card>
    );
  }

  if (!entries.length) {
    return null;
  }

  const motionApplied = entries.filter(
    (entry) => entry.summary.motion_style_applied === true,
  ).length;
  const motionSuppressed = entries.filter(
    (entry) =>
      entry.summary.motion_style_enabled === true &&
      entry.summary.motion_style_applied === false,
  ).length;
  const genericFaceMisses = entries.filter(isPossibleGenericFaceMiss).length;
  const providers = new Set(
    entries
      .map((entry) => entry.summary.concrete_provider ?? entry.summary.provider)
      .filter(Boolean),
  );
  const failedAttempts = entries.filter((entry) =>
    ["failed", "timed_out", "capacity_skipped", "qc_rejected"].includes(
      entry.summary.lifecycle_status ?? entry.attempt?.status ?? "",
    ),
  ).length;

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <CardTitle>I2V Prompt Diagnostics</CardTitle>
          <div className="flex flex-wrap gap-2">
            <Badge variant="outline">{entries.length} audits</Badge>
            <Badge variant={motionApplied ? "secondary" : "outline"}>
              {motionApplied} motion applied
            </Badge>
            <Badge variant={motionSuppressed ? "secondary" : "outline"}>
              {motionSuppressed} motion suppressed
            </Badge>
            {genericFaceMisses ? (
              <Badge variant="destructive">
                {genericFaceMisses} possible face misses
              </Badge>
            ) : null}
            {failedAttempts ? (
              <Badge variant="destructive">
                {failedAttempts} failed/skipped
              </Badge>
            ) : null}
            <Badge variant="outline">{providers.size} providers</Badge>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1180px] text-left text-sm">
            <thead className="text-xs uppercase text-muted-foreground">
              <tr>
                <th className="px-2 py-2 font-medium">Scene</th>
                <th className="px-2 py-2 font-medium">Attempt</th>
                <th className="px-2 py-2 font-medium">Provider</th>
                <th className="px-2 py-2 font-medium">Prompt channel</th>
                <th className="px-2 py-2 font-medium">Source / family</th>
                <th className="px-2 py-2 font-medium">Lifecycle</th>
                <th className="px-2 py-2 font-medium">Motion style</th>
                <th className="px-2 py-2 font-medium">Face-sensitive</th>
                <th className="px-2 py-2 font-medium">QC</th>
                <th className="px-2 py-2 font-medium">Asset</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((entry) => (
                <tr
                  key={entry.key}
                  className="border-t align-top hover:bg-muted/40"
                >
                  <td className="px-2 py-2">
                    {entry.summary.scene_id ?? "Unknown"}
                  </td>
                  <td className="px-2 py-2">
                    {entry.summary.attempt ?? "?"}
                    {entry.summary.max_attempts
                      ? `/${entry.summary.max_attempts}`
                      : ""}
                  </td>
                  <td className="px-2 py-2">
                    <div>
                      {entry.summary.concrete_provider ??
                        entry.summary.provider}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {entry.summary.model ?? entry.summary.route}
                    </div>
                  </td>
                  <td className="px-2 py-2">
                    {formatPromptChannel(entry.summary.prompt_channel) ??
                      "initial"}
                  </td>
                  <td className="px-2 py-2">
                    <div>
                      {i2vSourceFrameLabel(entry) ?? "source unavailable"}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {entry.summary.scene_prompt_source ?? "legacy"} /{" "}
                      {entry.summary.scene_prompt_family ?? "unknown family"}
                    </div>
                  </td>
                  <td className="px-2 py-2">
                    <Badge variant={i2vAttemptStatusVariant(entry)}>
                      {entry.summary.lifecycle_status ??
                        entry.attempt?.status ??
                        "resolved"}
                    </Badge>
                    {entry.summary.fallback || entry.attempt?.fallback ? (
                      <div className="mt-1 text-xs text-muted-foreground">
                        fallback from{" "}
                        {entry.summary.fallback_from ??
                          entry.attempt?.fallback_from ??
                          "primary"}
                      </div>
                    ) : null}
                    {entry.summary.error_code || entry.attempt?.error_code ? (
                      <div className="mt-1 break-words text-xs text-destructive">
                        {entry.summary.error_code ?? entry.attempt?.error_code}:{" "}
                        {entry.summary.error_message ??
                          entry.attempt?.error_message ??
                          "No provider detail"}
                      </div>
                    ) : null}
                  </td>
                  <td className="px-2 py-2">
                    <Badge
                      variant={
                        entry.summary.motion_style_applied
                          ? "secondary"
                          : "outline"
                      }
                    >
                      {entry.summary.motion_style_applied
                        ? "applied"
                        : (entry.summary.motion_style_suppressed_reason ??
                          "not applied")}
                    </Badge>
                  </td>
                  <td className="px-2 py-2">
                    <Badge
                      variant={
                        isPossibleGenericFaceMiss(entry)
                          ? "destructive"
                          : entry.summary.face_sensitive
                            ? "secondary"
                            : "outline"
                      }
                    >
                      {entry.summary.face_sensitive ? "yes" : "no"}
                    </Badge>
                    <div className="mt-1 text-xs text-muted-foreground">
                      {entry.summary.face_sensitive_reason ?? "unknown"}
                    </div>
                  </td>
                  <td className="px-2 py-2">
                    <div>
                      semantic{" "}
                      {entry.summary.semantic_enforcement_mode ?? "unknown"}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      expression {entry.summary.expression_qc_mode ?? "unknown"}
                    </div>
                  </td>
                  <td className="px-2 py-2">
                    <button
                      type="button"
                      className="text-left text-primary underline-offset-2 hover:underline"
                      onClick={() => setSelected(entry)}
                    >
                      {entry.summary.asset_s3_key ??
                        entry.media?.s3_key ??
                        "View prompts"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
      <I2VPromptAuditDialog
        entry={selected}
        onOpenChange={(open) => {
          if (!open) setSelected(null);
        }}
      />
    </Card>
  );
};

const I2VPromptAuditDialog = ({
  entry,
  onOpenChange,
}: {
  entry: I2VPromptAuditEntry | null;
  onOpenChange: (open: boolean) => void;
}) => {
  const prompts = entry?.audit?.prompts ?? null;
  const kling = objectValue(entry?.audit?.kling);
  return (
    <Dialog open={Boolean(entry)} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-3xl">
        {entry ? (
          <>
            <DialogHeader>
              <DialogTitle>
                Scene {entry.summary.scene_id ?? "?"} I2V Prompt
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 text-sm">
              <div className="grid gap-3 sm:grid-cols-2">
                <MetaItem label="Audit" value={entry.summary.audit_id} />
                <MetaItem label="Provider" value={entry.summary.provider} />
                <MetaItem
                  label="Concrete"
                  value={entry.summary.concrete_provider}
                />
                <MetaItem label="Model" value={entry.summary.model} />
                <MetaItem label="Task" value={entry.summary.task_id} />
                <MetaItem
                  label="Route contract"
                  value={entry.summary.route_contract_version}
                />
                <MetaItem
                  label="Contract digest"
                  value={entry.summary.route_contract_digest}
                />
                <MetaItem
                  label="Lifecycle"
                  value={
                    entry.summary.lifecycle_status ?? entry.attempt?.status
                  }
                />
                <MetaItem
                  label="Prompt source"
                  value={entry.summary.scene_prompt_source}
                />
                <MetaItem
                  label="Prompt family"
                  value={entry.summary.scene_prompt_family}
                />
                <MetaItem
                  label="Fallback from"
                  value={
                    entry.summary.fallback_from ?? entry.attempt?.fallback_from
                  }
                />
                <MetaItem
                  label="Error"
                  value={
                    entry.summary.error_message ?? entry.attempt?.error_message
                  }
                />
                <MetaItem
                  label="Prompt channel"
                  value={
                    formatPromptChannel(entry.summary.prompt_channel) ??
                    entry.summary.prompt_channel
                  }
                />
                <MetaItem
                  label="Motion style"
                  value={
                    entry.summary.motion_style_applied
                      ? "applied"
                      : entry.summary.motion_style_suppressed_reason
                  }
                />
                <MetaItem
                  label="Face-sensitive"
                  value={
                    entry.summary.face_sensitive
                      ? (entry.summary.face_sensitive_reason ?? "yes")
                      : (entry.summary.face_sensitive_reason ?? "no")
                  }
                />
              </div>
              <PromptTextBlock
                title="Final Positive Prompt"
                value={prompts?.final_positive_prompt}
                hash={entry.summary.positive_prompt_sha256}
              />
              <PromptTextBlock
                title="Negative Prompt"
                value={prompts?.negative_prompt}
                hash={entry.summary.negative_prompt_sha256}
              />
              <PromptTextBlock
                title="Source Preservation Prompt"
                value={prompts?.source_preservation_prompt}
              />
              <PromptTextBlock
                title="Kling Motion-Style Prompt"
                value={stringFromUnknown(kling?.motion_style_prompt)}
              />
              <JourneyFieldSection
                title="Classifier"
                value={{
                  face_sensitive: entry.summary.face_sensitive,
                  reason: entry.summary.face_sensitive_reason,
                  matched_subject_terms: entry.summary.matched_subject_terms,
                  matched_expression_terms:
                    entry.summary.matched_expression_terms,
                  generic_face_terms_enabled:
                    entry.summary.generic_face_terms_enabled,
                }}
              />
              <JourneyFieldSection
                title="Source / reference images"
                value={entry.summary.reference_frames}
              />
              <JourneyFieldSection
                title="Full Audit"
                value={entry.audit ?? entry.summary}
              />
            </div>
          </>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};

const PromptTextBlock = ({
  title,
  value,
  hash,
}: {
  title: string;
  value?: string | null;
  hash?: string | null;
}) => {
  if (!value && !hash) return null;
  return (
    <div>
      <div className="mb-2 flex flex-wrap items-center gap-2 font-medium">
        <span>{title}</span>
        {hash ? (
          <Badge variant="outline" className="font-mono text-[10px]">
            {hash.slice(0, 12)}
          </Badge>
        ) : null}
      </div>
      {value ? (
        <pre className="max-h-56 overflow-auto whitespace-pre-wrap rounded-md bg-muted p-3 text-xs">
          {value}
        </pre>
      ) : (
        <div className="rounded-md bg-muted px-3 py-2 text-xs text-muted-foreground">
          Prompt text was not included; hash is available.
        </div>
      )}
    </div>
  );
};

type ExpressionSummary = {
  mean?: number | null;
  late_mean?: number | null;
  face_present_rate?: number | null;
};

type ExpressionVerdict = {
  reason?: string | null;
  label?: string | null;
  late_mean?: number | null;
  face_present_rate?: number | null;
  t_flip?: number | null;
};

type ExpressionQCPayload = {
  scene_id?: string | number | null;
  attempt?: number | null;
  status?: string | null;
  action?: string | null;
  passed?: boolean | null;
  reason?: string | null;
  s3_key?: string | null;
  source_summary?: ExpressionSummary | null;
  generated_summary?: ExpressionSummary | null;
  verdict?: ExpressionVerdict | null;
  error?: string | null;
  metadata?: {
    prompt_channel?: string | null;
    prompt_config_hash?: string | null;
  } | null;
};

type ExpressionQCEntry = {
  node: EchoJourneyNode;
  payload: ExpressionQCPayload | null;
};

const ExpressionQCPanel = ({
  journey,
  isLoading,
}: {
  journey?: EchoflicksSessionJourney;
  isLoading?: boolean;
}) => {
  const qc = useMemo(() => buildExpressionQCSummary(journey), [journey]);
  const latestAction =
    qc.latest?.payload?.action ?? qc.latest?.node.status ?? "none";
  const latestReason =
    qc.latest?.payload?.reason ??
    qc.latest?.payload?.verdict?.reason ??
    qc.latest?.node.type;

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <CardTitle>I2V Expression QC</CardTitle>
          {qc.entries.length ? (
            <Badge
              variant={
                qc.failureCount > 0
                  ? "destructive"
                  : qc.retryCount > 0
                    ? "secondary"
                    : "outline"
              }
            >
              {latestAction}
            </Badge>
          ) : null}
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <EmptyHint>Loading expression QC...</EmptyHint>
        ) : qc.entries.length === 0 ? (
          <EmptyHint>
            No I2V expression QC checks are present for this Echo.
          </EmptyHint>
        ) : (
          <div className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <DebugMetric
                icon={<Smile className="size-4" />}
                label="Checks"
                value={`${qc.checkCount}`}
              />
              <DebugMetric
                icon={<RefreshCw className="size-4" />}
                label="Retries"
                value={`${qc.retryCount}`}
              />
              <DebugMetric
                icon={<AlertTriangle className="size-4" />}
                label="Failures"
                value={`${qc.failureCount}`}
              />
              <DebugMetric
                icon={<FileJson className="size-4" />}
                label="Report"
                value={qc.reportNode ? "Created" : "None"}
              />
            </div>

            <div className="overflow-hidden rounded-md border">
              {qc.entries.map(({ node, payload }) => (
                <div
                  key={node.id}
                  className="grid gap-3 border-t p-3 text-sm first:border-t-0 lg:grid-cols-[minmax(0,1fr)_minmax(180px,260px)_minmax(160px,220px)]"
                >
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="truncate font-medium">{node.label}</span>
                      <Badge variant={expressionStatusVariant(payload, node)}>
                        {payload?.status ?? node.status}
                      </Badge>
                    </div>
                    <div className="mt-1 text-xs text-muted-foreground">
                      {[
                        payload?.scene_id != null
                          ? `Scene ${payload.scene_id}`
                          : null,
                        payload?.attempt != null
                          ? `Attempt ${payload.attempt}`
                          : null,
                        formatPromptChannel(
                          payload?.metadata?.prompt_channel ??
                            node.decision?.prompt_channel,
                        ),
                        formatDateTime(node.occurred_at),
                      ]
                        .filter(Boolean)
                        .join(" / ")}
                    </div>
                    {payload?.s3_key ? (
                      <div className="mt-1 truncate text-xs text-muted-foreground">
                        {payload.s3_key}
                      </div>
                    ) : null}
                  </div>

                  <div className="min-w-0">
                    <div className="truncate font-medium">
                      {payload?.action ?? node.status}
                    </div>
                    <div className="mt-1 text-xs text-muted-foreground">
                      {payload?.verdict?.label ??
                        payload?.reason ??
                        latestReason ??
                        node.type}
                    </div>
                    {payload?.error ? (
                      <div className="mt-1 break-words text-xs text-destructive">
                        {payload.error}
                      </div>
                    ) : null}
                  </div>

                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <ExpressionMetric
                      label="Source smile"
                      value={formatExpressionScore(
                        payload?.source_summary?.mean,
                      )}
                    />
                    <ExpressionMetric
                      label="Late smile"
                      value={formatExpressionScore(
                        payload?.generated_summary?.late_mean ??
                          payload?.verdict?.late_mean,
                      )}
                    />
                    <ExpressionMetric
                      label="Face rate"
                      value={formatPercent(
                        payload?.generated_summary?.face_present_rate ??
                          payload?.verdict?.face_present_rate,
                      )}
                    />
                    <ExpressionMetric
                      label="Flip"
                      value={formatSeconds(payload?.verdict?.t_flip)}
                    />
                  </div>
                </div>
              ))}
            </div>

            {qc.reportNode ? (
              <div className="rounded-md border p-3 text-sm">
                <div className="font-medium">Expression QC report</div>
                <div className="mt-1 break-words text-muted-foreground">
                  {expressionReportLabel(qc.reportNode)}
                </div>
              </div>
            ) : null}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const ExpressionMetric = ({
  label,
  value,
}: {
  label: string;
  value: string;
}) => (
  <div className="rounded-md bg-muted px-2 py-1">
    <div className="text-muted-foreground">{label}</div>
    <div className="font-medium">{value}</div>
  </div>
);

type JourneyDebugStep = {
  node: EchoJourneyNode;
  elapsedMs: number;
};

type JourneyToolStep = {
  node: EchoJourneyNode;
  name: string;
  provider?: string | null;
  action?: string | null;
  elapsedMs?: number | null;
  status?: string | null;
};

const JourneyDebugPanel = ({
  journey,
  isLoading,
}: {
  journey?: EchoflicksSessionJourney;
  isLoading?: boolean;
}) => {
  const debug = useMemo(() => buildJourneyDebug(journey), [journey]);

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <CardTitle>Duration Debug</CardTitle>
          {debug.slowestStep ? (
            <Badge variant="secondary">
              Slowest {formatDurationMs(debug.slowestStep.elapsedMs)}
            </Badge>
          ) : null}
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <EmptyHint>Loading debug telemetry...</EmptyHint>
        ) : debug.nodesWithTelemetry === 0 ? (
          <EmptyHint>
            No timing or tool telemetry has been projected yet.
          </EmptyHint>
        ) : (
          <div className="space-y-5">
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <DebugMetric
                icon={<Timer className="size-4" />}
                label="Observed Step Time"
                value={formatDurationMs(debug.totalElapsedMs)}
              />
              <DebugMetric
                icon={<Activity className="size-4" />}
                label="Telemetry Nodes"
                value={`${debug.nodesWithTelemetry}`}
              />
              <DebugMetric
                icon={<Wrench className="size-4" />}
                label="Tools Used"
                value={`${debug.tools.length}`}
              />
              <DebugMetric
                icon={<Video className="size-4" />}
                label="Video Duration"
                value={debug.videoDurationLabel}
              />
            </div>

            {debug.slowestSteps.length > 0 ? (
              <DebugTable title="Slow Steps">
                {debug.slowestSteps.map((step) => (
                  <div
                    key={step.node.id}
                    className="grid gap-2 border-t py-2 text-sm sm:grid-cols-[minmax(0,1fr)_120px_140px]"
                  >
                    <div className="min-w-0">
                      <div className="truncate font-medium">
                        {step.node.label}
                      </div>
                      <div className="truncate text-xs text-muted-foreground">
                        {step.node.service ?? step.node.type}
                      </div>
                    </div>
                    <div>{formatDurationMs(step.elapsedMs)}</div>
                    <Badge
                      variant={
                        step.node.status === "failed"
                          ? "destructive"
                          : "outline"
                      }
                    >
                      {step.node.status}
                    </Badge>
                  </div>
                ))}
              </DebugTable>
            ) : null}

            {debug.tools.length > 0 ? (
              <DebugTable title="Tools">
                {debug.tools.slice(0, 8).map((tool, index) => (
                  <div
                    key={`${tool.node.id}-${tool.name}-${index}`}
                    className="grid gap-2 border-t py-2 text-sm sm:grid-cols-[minmax(0,1fr)_140px_120px]"
                  >
                    <div className="min-w-0">
                      <div className="truncate font-medium">{tool.name}</div>
                      <div className="truncate text-xs text-muted-foreground">
                        {tool.node.label}
                      </div>
                    </div>
                    <div className="truncate">
                      {tool.provider ?? tool.action ?? "local"}
                    </div>
                    <div>{formatDurationMs(tool.elapsedMs)}</div>
                  </div>
                ))}
              </DebugTable>
            ) : null}

            {debug.durationImpacts.length > 0 ? (
              <DebugTable title="Duration Impact">
                {debug.durationImpacts.map((node) => (
                  <div
                    key={node.id}
                    className="grid gap-2 border-t py-2 text-sm sm:grid-cols-[minmax(0,1fr)_120px_120px_120px]"
                  >
                    <div className="min-w-0">
                      <div className="truncate font-medium">{node.label}</div>
                      <div className="truncate text-xs text-muted-foreground">
                        {impactReason(node)}
                      </div>
                    </div>
                    <div>{formatSeconds(impactPlannedSeconds(node))}</div>
                    <div>{formatSeconds(impactActualSeconds(node))}</div>
                    <div>
                      {formatSeconds(node.duration_impact?.delta_seconds, true)}
                    </div>
                  </div>
                ))}
              </DebugTable>
            ) : null}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const DebugMetric = ({
  icon,
  label,
  value,
}: {
  icon: ReactNode;
  label: string;
  value: string;
}) => (
  <div className="rounded-md border p-3">
    <div className="flex items-center gap-2 text-xs font-medium uppercase text-muted-foreground">
      {icon}
      {label}
    </div>
    <div className="mt-2 truncate text-lg font-semibold">{value}</div>
  </div>
);

const DebugTable = ({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) => (
  <div>
    <div className="mb-2 text-sm font-medium">{title}</div>
    <div className="overflow-hidden rounded-md border">{children}</div>
  </div>
);

function buildJourneyDebug(journey?: EchoflicksSessionJourney) {
  const nodes = journey?.nodes ?? [];
  const timedSteps: JourneyDebugStep[] = nodes
    .map((node) => ({ node, elapsedMs: durationMsForNode(node) }))
    .filter((step) => step.elapsedMs > 0)
    .sort((left, right) => right.elapsedMs - left.elapsedMs);
  const tools: JourneyToolStep[] = nodes.flatMap((node) =>
    (node.tools ?? []).map((tool) => ({
      node,
      name: tool.name,
      provider: tool.provider,
      action: tool.action ?? tool.operation,
      elapsedMs: tool.elapsed_ms ?? tool.latency_ms,
      status: tool.status,
    })),
  );
  const durationImpacts = nodes.filter((node) => node.duration_impact);
  const totalElapsedMs = timedSteps.reduce(
    (sum, step) => sum + step.elapsedMs,
    0,
  );
  const finalDuration = durationImpacts
    .map((node) => impactActualSeconds(node))
    .find((value): value is number => typeof value === "number");
  const targetDuration =
    journey?.summary?.target_video_seconds ??
    durationImpacts
      .map(
        (node) =>
          node.duration_impact?.target_video_seconds ??
          node.duration_impact?.target_seconds,
      )
      .find((value): value is number => typeof value === "number");

  return {
    nodesWithTelemetry: nodes.filter(
      (node) =>
        durationMsForNode(node) > 0 ||
        (node.tools?.length ?? 0) > 0 ||
        Boolean(node.duration_impact),
    ).length,
    slowestStep: timedSteps[0],
    slowestSteps: timedSteps.slice(0, 5),
    tools,
    durationImpacts,
    totalElapsedMs,
    videoDurationLabel:
      finalDuration != null || targetDuration != null
        ? `${formatSeconds(finalDuration)} / ${formatSeconds(targetDuration)}`
        : "Unknown",
  };
}

function buildExpressionQCSummary(journey?: EchoflicksSessionJourney) {
  const entries: ExpressionQCEntry[] = (journey?.nodes ?? [])
    .filter(isExpressionQCNode)
    .map((node) => ({ node, payload: expressionQCPayload(node) }))
    .sort((left, right) =>
      String(left.node.occurred_at ?? left.node.id).localeCompare(
        String(right.node.occurred_at ?? right.node.id),
      ),
    );
  const reportNode =
    entries.find((entry) => entry.node.type === "expression_qc.report.created")
      ?.node ?? null;
  const checkCount = entries.filter((entry) => entry.payload).length;
  const retryCount = entries.filter(
    (entry) =>
      entry.node.type.includes("retry") ||
      entry.payload?.action === "retry_requested",
  ).length;
  const failureCount = entries.filter(
    (entry) =>
      entry.payload?.status === "failed" ||
      entry.payload?.status === "error" ||
      entry.node.status === "failed",
  ).length;

  return {
    entries,
    latest: entries.at(-1) ?? null,
    reportNode,
    checkCount,
    retryCount,
    failureCount,
  };
}

function isExpressionQCNode(node: EchoJourneyNode) {
  return (
    node.type.startsWith("scene.expression_qc") ||
    node.type === "expression_qc.report.created" ||
    Boolean(expressionQCPayload(node))
  );
}

function expressionQCPayload(
  node: EchoJourneyNode,
): ExpressionQCPayload | null {
  const rawPayload = objectValue(node.raw_payload);
  const expression = objectValue(rawPayload?.expression_qc);
  return expression ? (expression as ExpressionQCPayload) : null;
}

function objectValue(value: unknown): Record<string, unknown> | null {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function stringFromUnknown(value: unknown): string | null {
  return typeof value === "string" && value ? value : null;
}

function numberFromUnknown(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim()) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function booleanFromUnknown(value: unknown): boolean | null {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value !== 0;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (["true", "1", "yes"].includes(normalized)) return true;
    if (["false", "0", "no", ""].includes(normalized)) return false;
  }
  return null;
}

function stringListFromUnknown(value: unknown): string[] {
  return Array.isArray(value)
    ? value
        .filter(
          (item): item is string | number | boolean =>
            typeof item === "string" ||
            typeof item === "number" ||
            typeof item === "boolean",
        )
        .map(String)
    : [];
}

function summarizeI2VPromptAudit(
  audit: I2VPromptAudit | Record<string, unknown> | null,
): I2VPromptAuditSummary | null {
  if (!audit) return null;
  const provider = objectValue(audit.provider);
  const kling = objectValue(audit.kling);
  const prompts = objectValue(audit.prompts);
  const qcContext = objectValue(audit.qc_context);
  const lifecycle = objectValue(audit.lifecycle);
  const routeContract = objectValue(audit.route_contract);
  const asset = objectValue(audit.asset);
  const authoring = objectValue(audit.authoring);
  return normalizeI2VPromptAuditSummary({
    schema_version: audit.schema_version,
    audit_id: audit.audit_id,
    scene_id: audit.scene_id,
    source_scene_type: audit.source_scene_type,
    attempt: audit.attempt,
    max_attempts: audit.max_attempts,
    prompt_channel: audit.prompt_channel,
    route_contract_version: routeContract?.version,
    route_contract_digest: routeContract?.digest,
    provider: provider?.requested_provider,
    route: provider?.route,
    concrete_provider: provider?.concrete_provider,
    model: provider?.model,
    model_version: provider?.model_version,
    mode: provider?.mode,
    task_id: provider?.task_id,
    positive_prompt_sha256: prompts?.positive_prompt_sha256,
    negative_prompt_sha256: prompts?.negative_prompt_sha256,
    negative_prompt_source: prompts?.negative_prompt_source,
    scene_prompt_source: prompts?.scene_prompt_source,
    scene_prompt_family: prompts?.scene_prompt_family,
    motion_style_enabled: kling?.motion_style_enabled,
    motion_style_applied: kling?.motion_style_applied,
    motion_style_suppressed_reason: kling?.motion_style_suppressed_reason,
    face_sensitive: kling?.face_sensitive,
    face_sensitive_reason: kling?.face_sensitive_reason,
    matched_subject_terms: kling?.matched_subject_terms,
    matched_expression_terms: kling?.matched_expression_terms,
    generic_face_terms_enabled: kling?.generic_face_terms_enabled,
    semantic_enforcement_mode: qcContext?.semantic_enforcement_mode,
    expression_qc_mode: qcContext?.expression_qc_mode,
    reference_frames: audit.reference_frames,
    asset_s3_key: asset?.s3_key,
    asset_disposition: asset?.disposition,
    lifecycle_status: lifecycle?.status,
    lifecycle_terminal: lifecycle?.terminal,
    error_code: lifecycle?.error_code,
    error_message: lifecycle?.error_message,
    retryable: lifecycle?.retryable,
    fallback: lifecycle?.fallback,
    fallback_from: lifecycle?.fallback_from,
    correction_validation: audit.correction_validation,
    authoring_audit_id: authoring?.audit_id,
    authoring_run_id: authoring?.authoring_run_id,
    authoring_target_key: authoring?.target_key ?? authoring?.overlay_key,
    authoring_status: authoring?.status,
    authoring_correlation: authoring?.correlation_status,
    authoring_prompt_sha256: authoring?.prompt_sha256,
    authoring_guide_version: authoring?.guide_version,
    authoring_guide_digest: authoring?.guide_digest,
    authoring_vision_digest: authoring?.vision_digest,
    authoring_shot_list_digest: authoring?.shot_list_digest,
    route_role: authoring?.route_role,
    lock_compliant: authoring?.lock_compliant,
  });
}

function normalizeI2VPromptAuditSummary(
  value: unknown,
): I2VPromptAuditSummary | null {
  const record = objectValue(value);
  if (!record || Object.keys(record).length === 0) return null;
  const correctionValidation = objectValue(record.correction_validation);
  return {
    schema_version: stringFromUnknown(record.schema_version),
    audit_id: stringFromUnknown(record.audit_id),
    scene_id:
      typeof record.scene_id === "number" || typeof record.scene_id === "string"
        ? record.scene_id
        : null,
    source_scene_type: stringFromUnknown(record.source_scene_type),
    attempt: numberFromUnknown(record.attempt),
    max_attempts: numberFromUnknown(record.max_attempts),
    prompt_channel: stringFromUnknown(record.prompt_channel),
    route_contract_version: stringFromUnknown(record.route_contract_version),
    route_contract_digest: stringFromUnknown(record.route_contract_digest),
    provider: stringFromUnknown(record.provider),
    route: stringFromUnknown(record.route),
    concrete_provider: stringFromUnknown(record.concrete_provider),
    model: stringFromUnknown(record.model),
    model_version: stringFromUnknown(record.model_version),
    mode: stringFromUnknown(record.mode),
    task_id: stringFromUnknown(record.task_id),
    positive_prompt_sha256: stringFromUnknown(record.positive_prompt_sha256),
    negative_prompt_sha256: stringFromUnknown(record.negative_prompt_sha256),
    negative_prompt_source: stringFromUnknown(record.negative_prompt_source),
    scene_prompt_source: stringFromUnknown(record.scene_prompt_source),
    scene_prompt_family: stringFromUnknown(record.scene_prompt_family),
    motion_style_enabled: booleanFromUnknown(record.motion_style_enabled),
    motion_style_applied: booleanFromUnknown(record.motion_style_applied),
    motion_style_suppressed_reason: stringFromUnknown(
      record.motion_style_suppressed_reason,
    ),
    face_sensitive: booleanFromUnknown(record.face_sensitive),
    face_sensitive_reason: stringFromUnknown(record.face_sensitive_reason),
    matched_subject_terms: stringListFromUnknown(record.matched_subject_terms),
    matched_expression_terms: stringListFromUnknown(
      record.matched_expression_terms,
    ),
    generic_face_terms_enabled: booleanFromUnknown(
      record.generic_face_terms_enabled,
    ),
    semantic_enforcement_mode: stringFromUnknown(
      record.semantic_enforcement_mode,
    ),
    expression_qc_mode: stringFromUnknown(record.expression_qc_mode),
    reference_frames: objectValue(record.reference_frames),
    asset_s3_key: stringFromUnknown(record.asset_s3_key),
    asset_disposition: stringFromUnknown(record.asset_disposition),
    lifecycle_status: stringFromUnknown(record.lifecycle_status),
    lifecycle_terminal: booleanFromUnknown(record.lifecycle_terminal),
    error_code: stringFromUnknown(record.error_code),
    error_message: stringFromUnknown(record.error_message),
    retryable: booleanFromUnknown(record.retryable),
    fallback: booleanFromUnknown(record.fallback),
    fallback_from: stringFromUnknown(record.fallback_from),
    authoring_audit_id: stringFromUnknown(record.authoring_audit_id),
    authoring_run_id: stringFromUnknown(record.authoring_run_id),
    authoring_target_key: stringFromUnknown(record.authoring_target_key),
    authoring_status: stringFromUnknown(record.authoring_status),
    authoring_correlation: stringFromUnknown(record.authoring_correlation),
    authoring_prompt_sha256: stringFromUnknown(record.authoring_prompt_sha256),
    authoring_guide_version: stringFromUnknown(record.authoring_guide_version),
    authoring_guide_digest: stringFromUnknown(record.authoring_guide_digest),
    authoring_vision_digest: stringFromUnknown(record.authoring_vision_digest),
    authoring_shot_list_digest: stringFromUnknown(
      record.authoring_shot_list_digest,
    ),
    route_role: stringFromUnknown(record.route_role),
    lock_compliant: booleanFromUnknown(record.lock_compliant),
    correction_validation: correctionValidation
      ? {
          phase: stringFromUnknown(correctionValidation.phase),
          status: stringFromUnknown(correctionValidation.status),
          reason_code: stringFromUnknown(correctionValidation.reason_code),
          error: stringFromUnknown(correctionValidation.error),
          submitted: booleanFromUnknown(correctionValidation.submitted),
          semantic_attempt_ordinal: numberFromUnknown(
            correctionValidation.semantic_attempt_ordinal,
          ),
        }
      : null,
  };
}

// eslint-disable-next-line react-refresh/only-export-components -- pure fixture seam
export function collectI2VPromptAuditEntries(
  journey: EchoflicksSessionJourney | undefined,
  media: EchoflicksSessionMedia[],
): I2VPromptAuditEntry[] {
  const entries = new Map<string, I2VPromptAuditEntry>();
  const put = (entry: I2VPromptAuditEntry) => {
    const existing = entries.get(entry.key);
    if (!existing) {
      entries.set(entry.key, entry);
      return;
    }
    entries.set(entry.key, {
      ...existing,
      summary: mergeDefinedSummary(
        existing.summary as Record<string, unknown>,
        entry.summary as Record<string, unknown>,
      ) as I2VPromptAuditSummary,
      audit: existing.audit ?? entry.audit,
      node: existing.node ?? entry.node,
      media: existing.media ?? entry.media,
      attempt: existing.attempt ?? entry.attempt,
    });
  };

  for (const node of journey?.nodes ?? []) {
    const rawPayload = objectValue(node.raw_payload);
    const audit = (node.i2v_prompt_audit ??
      objectValue(rawPayload?.i2v_prompt_audit)) as I2VPromptAudit | null;
    const summary =
      node.i2v_prompt_audit_summary ??
      normalizeI2VPromptAuditSummary(rawPayload?.i2v_prompt_audit_summary) ??
      summarizeI2VPromptAudit(audit) ??
      (node.type === "i2v_prompt"
        ? normalizeI2VPromptAuditSummary(node.decision)
        : null);
    if (!summary) continue;
    put({
      key: promptAuditEntryKey(summary, node.id),
      summary,
      audit,
      node,
      media: null,
      attempt: null,
    });
  }

  for (const attempt of journey?.i2v_prompt_attempts ?? []) {
    const summary = {
      ...(attempt.prompt_audit_summary ?? {}),
      audit_id: attempt.audit_id,
      scene_id: attempt.scene_id ?? attempt.prompt_audit_summary?.scene_id,
      attempt: attempt.attempt ?? attempt.prompt_audit_summary?.attempt,
      max_attempts:
        attempt.max_attempts ?? attempt.prompt_audit_summary?.max_attempts,
      provider: attempt.provider ?? attempt.prompt_audit_summary?.provider,
      concrete_provider:
        attempt.concrete_provider ??
        attempt.prompt_audit_summary?.concrete_provider,
      model: attempt.model ?? attempt.prompt_audit_summary?.model,
      task_id: attempt.task_id ?? attempt.prompt_audit_summary?.task_id,
      asset_s3_key:
        attempt.asset_s3_key ?? attempt.prompt_audit_summary?.asset_s3_key,
      lifecycle_status:
        attempt.status ?? attempt.prompt_audit_summary?.lifecycle_status,
      lifecycle_terminal:
        attempt.terminal ?? attempt.prompt_audit_summary?.lifecycle_terminal,
      error_code:
        attempt.error_code ?? attempt.prompt_audit_summary?.error_code,
      error_message:
        attempt.error_message ?? attempt.prompt_audit_summary?.error_message,
      retryable: attempt.retryable ?? attempt.prompt_audit_summary?.retryable,
      fallback: attempt.fallback ?? attempt.prompt_audit_summary?.fallback,
      fallback_from:
        attempt.fallback_from ?? attempt.prompt_audit_summary?.fallback_from,
    } satisfies I2VPromptAuditSummary;
    put({
      key: promptAuditEntryKey(summary, attempt.audit_id),
      summary,
      audit: null,
      node: null,
      media: null,
      attempt,
    });
  }

  for (const item of media) {
    const summary = item.i2v_prompt_audit_summary;
    if (!summary) continue;
    put({
      key: promptAuditEntryKey(summary, String(item.id)),
      summary: {
        ...summary,
        asset_s3_key: summary.asset_s3_key ?? item.s3_key,
      },
      audit: null,
      node: null,
      media: item,
      attempt: null,
    });
  }

  return [...entries.values()].sort((left, right) => {
    const sceneDelta =
      Number(left.summary.scene_id ?? 0) - Number(right.summary.scene_id ?? 0);
    if (Number.isFinite(sceneDelta) && sceneDelta !== 0) return sceneDelta;
    return (left.summary.attempt ?? 0) - (right.summary.attempt ?? 0);
  });
}

function i2vAttemptStatusVariant(entry: I2VPromptAuditEntry) {
  const status = entry.summary.lifecycle_status ?? entry.attempt?.status;
  if (
    ["failed", "timed_out", "capacity_skipped", "qc_rejected"].includes(
      status ?? "",
    )
  ) {
    return "destructive" as const;
  }
  if (["succeeded", "reused"].includes(status ?? ""))
    return "secondary" as const;
  return "outline" as const;
}

function i2vSourceFrameLabel(entry: I2VPromptAuditEntry) {
  const frames = objectValue(entry.summary.reference_frames);
  const first = objectValue(frames?.first_frame);
  return stringFromUnknown(first?.file_name ?? first?.storage_key);
}

function promptAuditEntryKey(summary: I2VPromptAuditSummary, fallback: string) {
  return (
    summary.audit_id ??
    `scene:${summary.scene_id ?? "unknown"}:attempt:${summary.attempt ?? "unknown"}:${fallback}`
  );
}

function isPossibleGenericFaceMiss(entry: I2VPromptAuditEntry) {
  const summary = entry.summary;
  return (
    summary.face_sensitive === false &&
    summary.generic_face_terms_enabled === false &&
    (summary.matched_expression_terms?.length ?? 0) > 0
  );
}

function expressionStatusVariant(
  payload: ExpressionQCPayload | null,
  node: EchoJourneyNode,
) {
  const status = payload?.status ?? node.status;
  if (status === "failed" || status === "error") return "destructive";
  if (status === "skipped") return "secondary";
  if (node.status === "degraded" || payload?.action === "retry_requested") {
    return "secondary";
  }
  return "outline";
}

function formatExpressionScore(value?: number | null) {
  if (typeof value !== "number" || !Number.isFinite(value)) return "Unknown";
  return value.toFixed(2);
}

function formatPercent(value?: number | null) {
  if (typeof value !== "number" || !Number.isFinite(value)) return "Unknown";
  return `${Math.round(value * 100)}%`;
}

function formatPromptChannel(value: unknown) {
  return typeof value === "string" && value ? value.replaceAll("_", " ") : null;
}

function expressionReportLabel(node: EchoJourneyNode) {
  const rawPayload = objectValue(node.raw_payload);
  const reportKey = rawPayload?.report_s3_key;
  return (
    node.outputs?.[0]?.s3_key ??
    (typeof reportKey === "string" ? reportKey : null) ??
    "Report node created"
  );
}

const JourneyNodeDialog = ({
  node,
  onOpenChange,
}: {
  node: EchoJourneyNode | null;
  onOpenChange: (open: boolean) => void;
}) => (
  <Dialog open={Boolean(node)} onOpenChange={onOpenChange}>
    <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
      {node ? (
        <>
          <DialogHeader>
            <DialogTitle className="flex flex-wrap items-center gap-2">
              {node.label}
              <EditChannelChip badge={editChannelBadge(node)} />
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 text-sm">
            <div className="grid gap-3 sm:grid-cols-2">
              <MetaItem label="Status" value={node.status} />
              <MetaItem label="Service" value={node.service} />
              <MetaItem label="Type" value={node.type} />
              <MetaItem
                label="Occurred"
                value={formatDateTime(node.occurred_at)}
              />
              <MetaItem
                label="Completed"
                value={formatDateTime(node.completed_at)}
              />
              <MetaItem
                label="Elapsed"
                value={formatDurationMs(durationMsForNode(node))}
              />
              <MetaItem label="Confidence" value={node.confidence} />
              <MetaItem label="Workload" value={node.workload_id} />
            </div>
            <JourneyI2VPromptSection node={node} />
            <JourneyFieldSection title="Decision" value={node.decision} />
            <JourneyToolSection title="Tools" node={node} />
            <JourneyFieldSection title="Timing" value={node.timing} />
            <JourneyFieldSection
              title="Duration Impact"
              value={node.duration_impact}
            />
            <JourneyNotesSection notes={node.debug_notes ?? []} />
            <JourneyListSection title="Inputs" items={node.inputs ?? []} />
            <JourneyListSection title="Outputs" items={node.outputs ?? []} />
            <JourneyListSection
              title="Artifacts"
              items={node.artifacts ?? []}
            />
            <JourneyFieldSection title="Raw Payload" value={node.raw_payload} />
          </div>
        </>
      ) : null}
    </DialogContent>
  </Dialog>
);

const JourneyI2VPromptSection = ({ node }: { node: EchoJourneyNode }) => {
  const rawPayload = objectValue(node.raw_payload);
  const audit = (node.i2v_prompt_audit ??
    objectValue(rawPayload?.i2v_prompt_audit)) as I2VPromptAudit | null;
  const summary =
    node.i2v_prompt_audit_summary ??
    normalizeI2VPromptAuditSummary(rawPayload?.i2v_prompt_audit_summary) ??
    summarizeI2VPromptAudit(audit) ??
    (node.type === "i2v_prompt"
      ? normalizeI2VPromptAuditSummary(node.decision)
      : null);
  if (!audit && !summary) return null;
  const prompts = audit?.prompts ?? null;
  return (
    <div className="space-y-3 rounded-md border p-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="font-medium">I2V Prompt Audit</div>
        {summary?.motion_style_applied ? (
          <Badge variant="secondary">motion applied</Badge>
        ) : summary?.motion_style_suppressed_reason ? (
          <Badge variant="outline">
            {summary.motion_style_suppressed_reason}
          </Badge>
        ) : null}
        {summary?.face_sensitive ? (
          <Badge variant="secondary">face-sensitive</Badge>
        ) : null}
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <MetaItem label="Audit" value={summary?.audit_id} />
        <MetaItem
          label="Provider"
          value={summary?.concrete_provider ?? summary?.provider}
        />
        <MetaItem label="Scene" value={summary?.scene_id} />
        <MetaItem label="Attempt" value={summary?.attempt} />
      </div>
      <PromptTextBlock
        title="Final Positive Prompt"
        value={prompts?.final_positive_prompt}
        hash={summary?.positive_prompt_sha256}
      />
      <PromptTextBlock
        title="Negative Prompt"
        value={prompts?.negative_prompt}
        hash={summary?.negative_prompt_sha256}
      />
    </div>
  );
};

const JourneyFieldSection = ({
  title,
  value,
}: {
  title: string;
  value?: object | null;
}) => {
  if (!value || Object.keys(value).length === 0) return null;
  return (
    <div>
      <div className="mb-2 font-medium">{title}</div>
      <pre className="max-h-52 overflow-auto rounded-md bg-muted p-3 text-xs">
        {JSON.stringify(value, null, 2)}
      </pre>
    </div>
  );
};

const JourneyToolSection = ({
  title,
  node,
}: {
  title: string;
  node: EchoJourneyNode;
}) => {
  const tools = node.tools ?? [];
  if (!tools.length) return null;
  return (
    <div>
      <div className="mb-2 font-medium">{title}</div>
      <div className="space-y-2">
        {tools.map((tool, index) => (
          <div
            key={`${tool.name}-${index}`}
            className="rounded-md bg-muted p-2"
          >
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="font-medium">{tool.name}</div>
              <div className="text-xs text-muted-foreground">
                {formatDurationMs(tool.elapsed_ms ?? tool.latency_ms)}
              </div>
            </div>
            <div className="mt-1 break-words text-muted-foreground">
              {[tool.provider, tool.action ?? tool.operation, tool.status]
                .filter(Boolean)
                .join(" / ") ||
                tool.category ||
                "tool"}
            </div>
            {tool.metadata ? (
              <pre className="mt-2 max-h-40 overflow-auto rounded bg-background/70 p-2 text-xs">
                {JSON.stringify(tool.metadata, null, 2)}
              </pre>
            ) : null}
          </div>
        ))}
      </div>
    </div>
  );
};

const JourneyNotesSection = ({ notes }: { notes: string[] }) => {
  if (!notes.length) return null;
  return (
    <div>
      <div className="mb-2 font-medium">Debug Notes</div>
      <div className="space-y-1 text-sm text-muted-foreground">
        {notes.map((note, index) => (
          <div
            key={`${note}-${index}`}
            className="rounded-md bg-muted px-2 py-1"
          >
            {note}
          </div>
        ))}
      </div>
    </div>
  );
};

const JourneyListSection = ({
  title,
  items,
}: {
  title: string;
  items: NonNullable<EchoJourneyNode["inputs"]>;
}) => {
  if (!items.length) return null;
  return (
    <div>
      <div className="mb-2 font-medium">{title}</div>
      <div className="space-y-2">
        {items.map((item, index) => (
          <div
            key={`${item.label}-${index}`}
            className="rounded-md bg-muted p-2"
          >
            <div className="font-medium">{item.label}</div>
            <div className="break-words text-muted-foreground">
              {item.s3_key ?? item.value ?? item.summary ?? item.kind}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const JourneyStatusIcon = ({ status }: { status?: string | null }) => {
  if (status === "succeeded")
    return <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-emerald-600" />;
  if (status === "running")
    return <Clock3 className="mt-0.5 size-4 shrink-0 text-sky-600" />;
  if (status === "waiting_for_user")
    return <PauseCircle className="mt-0.5 size-4 shrink-0 text-amber-600" />;
  if (status === "degraded")
    return <AlertTriangle className="mt-0.5 size-4 shrink-0 text-amber-600" />;
  if (status === "failed")
    return <XCircle className="mt-0.5 size-4 shrink-0 text-destructive" />;
  if (status === "cancelled")
    return (
      <GitBranch className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
    );
  return <Circle className="mt-0.5 size-4 shrink-0 text-muted-foreground" />;
};

const durationMsForNode = (node: EchoJourneyNode): number => {
  if (
    typeof node.timing?.elapsed_ms === "number" ||
    typeof node.timing?.latency_ms === "number"
  ) {
    return node.timing.elapsed_ms ?? node.timing.latency_ms ?? 0;
  }
  if (node.occurred_at && node.completed_at) {
    const started = Date.parse(node.occurred_at);
    const completed = Date.parse(node.completed_at);
    if (Number.isFinite(started) && Number.isFinite(completed)) {
      return Math.max(0, completed - started);
    }
  }
  return 0;
};

const impactPlannedSeconds = (node: EchoJourneyNode) =>
  node.duration_impact?.planned_scene_seconds ??
  node.duration_impact?.planned_seconds ??
  node.duration_impact?.expected_video_seconds;

const impactActualSeconds = (node: EchoJourneyNode) =>
  node.duration_impact?.actual_video_seconds ??
  node.duration_impact?.output_seconds ??
  node.duration_impact?.measured_seconds ??
  node.duration_impact?.generated_asset_seconds ??
  node.duration_impact?.moviepy_duration_seconds;

const impactReason = (node: EchoJourneyNode) =>
  node.duration_impact?.action ??
  node.duration_impact?.reason ??
  node.duration_impact?.fallback_reason ??
  node.duration_impact?.duration_qc_status ??
  "duration telemetry";

const formatDurationMs = (value?: number | null) => {
  if (typeof value !== "number" || !Number.isFinite(value) || value <= 0) {
    return "Unknown";
  }
  if (value < 1000) {
    return `${value}ms`;
  }
  const seconds = value / 1000;
  if (seconds < 60) {
    return `${seconds.toFixed(seconds < 10 ? 1 : 0)}s`;
  }
  const minutes = Math.floor(seconds / 60);
  const remainder = Math.round(seconds % 60);
  return `${minutes}m ${remainder}s`;
};

const formatSeconds = (value?: number | null, signed = false) => {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return "Unknown";
  }
  const prefix = signed && value > 0 ? "+" : "";
  return `${prefix}${value.toFixed(Math.abs(value) < 10 ? 1 : 0)}s`;
};

const generatedSceneAssetKinds = new Set([
  "generated_asset",
  "i2v",
  "transition_whip",
  "title_card",
  "map_clip",
  "drone_shot",
]);

const isGeneratedSceneAsset = (media: EchoflicksSessionMedia) =>
  generatedSceneAssetKinds.has(media.kind) ||
  generatedSceneAssetKinds.has(media.asset_role ?? "");

const journeyNodeClass = (status?: string | null) => {
  if (status === "failed") return "border-destructive/40 bg-destructive/5";
  if (status === "degraded" || status === "waiting_for_user") {
    return "border-amber-300 bg-amber-50";
  }
  if (status === "running") return "border-sky-300 bg-sky-50";
  if (status === "succeeded") return "border-emerald-200 bg-emerald-50/60";
  return "bg-background";
};

const MetaItem = ({
  label,
  value,
}: {
  label: string;
  value?: ReactNode | null;
}) => {
  const displayValue = value === "" || value == null ? "Unknown" : value;
  return (
    <div>
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="mt-1 break-words font-medium">{displayValue}</dd>
    </div>
  );
};

const MediaGrid = ({ media }: { media: EchoflicksSessionMedia[] }) => (
  <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
    {media.map((item) => (
      <div key={item.id} className="overflow-hidden rounded-md border">
        <MediaPreview media={item} />
        <div className="space-y-1 p-3 text-sm">
          <div className="truncate font-medium">
            {item.s3_key?.split("/").at(-1) ?? item.local_path}
          </div>
          <div className="text-muted-foreground">
            {[humanizeAssetRole(item.asset_role), formatBytes(item.size_bytes)]
              .filter(Boolean)
              .join(" · ")}
          </div>
        </div>
      </div>
    ))}
  </div>
);

const MediaPreview = ({ media }: { media: EchoflicksSessionMedia }) => {
  const mediaUrl = useDisplayableImage(media);

  if (mediaUrl.url && isImage(media.mime_type)) {
    return (
      <div className="relative">
        <img
          src={mediaUrl.url}
          alt={media.s3_key ?? "Uploaded media"}
          className="aspect-video w-full bg-black object-contain"
          loading="lazy"
          onError={mediaUrl.refresh}
        />
        <DisplayCropBadge label="Full frame · contain" isDisplayCrop={false} />
      </div>
    );
  }

  if (mediaUrl.url && isVideo(media.mime_type)) {
    return (
      <video
        src={mediaUrl.url}
        controls
        onError={mediaUrl.refresh}
        className="aspect-video w-full bg-black object-contain"
      />
    );
  }

  if (mediaUrl.url && isAudio(media.mime_type)) {
    return (
      <div className="flex aspect-video items-center justify-center bg-muted p-3">
        <audio
          src={mediaUrl.url}
          controls
          className="w-full"
          onError={mediaUrl.refresh}
        />
      </div>
    );
  }

  return (
    <div className="flex aspect-video items-center justify-center bg-muted text-muted-foreground">
      <MediaIcon media={media} />
    </div>
  );
};

// "Listing Ingest" — the per-attempt ExtractionRecord the bot publishes when a
// real-estate listing URL is ingested: which extractor/tier won (tier0 /
// WPResidence / LLM / recipe), the fields it found (with grounding ticks), every
// image accept/reject decision, the QC verdict and timings. When a recipe served
// with the LLM running in shadow (or the session holds several attempts), the
// Compare toggle diffs the field maps — the recipe-vs-LLM quality audit.
const GROUNDING_KEY_BY_FIELD: Record<string, string> = {
  price_display: "price",
  bedrooms: "bedrooms",
  bathrooms: "bathrooms",
  area: "area",
};

const GroundingTick = ({ value }: { value: boolean | null | undefined }) => {
  if (value === true)
    return (
      <CheckCircle2
        className="size-3.5 text-emerald-600"
        aria-label="grounded"
      />
    );
  if (value === false)
    return (
      <XCircle
        className="size-3.5 text-red-500"
        aria-label="not found on page"
      />
    );
  return null;
};

const IngestFieldTable = ({
  ingest,
  compareWith,
  compareLabel,
}: {
  ingest: EchoflicksListingIngest;
  compareWith?: Record<string, unknown> | null;
  compareLabel?: string;
}) => {
  const fields = ingest.fields ?? {};
  const diffKeys = compareWith
    ? new Set(diffIngestFieldKeys(fields, compareWith))
    : new Set<string>();
  return (
    <div className="overflow-hidden rounded-md border">
      <table className="w-full text-sm">
        {compareWith ? (
          <thead>
            <tr className="border-b bg-muted/50 text-left text-xs text-muted-foreground">
              <th className="px-3 py-1.5 font-medium">Field</th>
              <th className="px-3 py-1.5 font-medium">
                {ingest.extractor ?? "primary"}
              </th>
              <th className="px-3 py-1.5 font-medium">
                {compareLabel ?? "comparison"}
              </th>
            </tr>
          </thead>
        ) : null}
        <tbody>
          {INGEST_FIELD_META.map(({ key, label }) => {
            const mismatch = diffKeys.has(key);
            return (
              <tr
                key={key}
                className={`border-b last:border-b-0 ${mismatch ? "bg-amber-50 dark:bg-amber-950/30" : ""}`}
              >
                <td className="px-3 py-1.5 text-muted-foreground">{label}</td>
                <td className="px-3 py-1.5">
                  <span className="inline-flex items-center gap-1.5">
                    {ingestFieldDisplay(fields[key])}
                    <GroundingTick
                      value={
                        GROUNDING_KEY_BY_FIELD[key]
                          ? ingest.grounding?.[GROUNDING_KEY_BY_FIELD[key]]
                          : null
                      }
                    />
                  </span>
                </td>
                {compareWith ? (
                  <td className="px-3 py-1.5">
                    {ingestFieldDisplay(compareWith[key])}
                  </td>
                ) : null}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const ListingIngestCard = ({
  media,
  session = null,
  events = [],
}: {
  media: EchoflicksSessionMedia[];
  /** Echo row — carries the indexer's flat property-type projection (FPV-032). */
  session?: EchoflicksVideoSession | null;
  /** Session events — the `property.type_resolved` fallback for older rows. */
  events?: EchoflicksSessionEvent[];
}) => {
  const records = ingestRecordsOf(media);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showCompare, setShowCompare] = useState(false);
  const [showRaw, setShowRaw] = useState(false);

  if (records.length === 0) return null;
  const primary = primaryIngestRecord(records);
  const selected =
    records.find((row) => String(row.id) === selectedId) ??
    primary ??
    records[0];
  const ingest = selected.ingest as EchoflicksListingIngest;

  // Compare target: the in-record LLM shadow (recipe runs), else another record.
  const otherRecord = records.find((row) => row.id !== selected.id) ?? null;
  const shadowFields = ingest.shadow?.fields ?? null;
  const compareFields =
    shadowFields ??
    (otherRecord?.ingest?.fields as Record<string, unknown>) ??
    null;
  const compareLabel = shadowFields
    ? `${ingest.shadow?.extractor ?? "LLM"} (shadow)`
    : (otherRecord?.ingest?.extractor ?? "previous attempt");
  const canCompare = Boolean(compareFields);

  const images = ingest.images ?? {};
  const inventory = ingest.inventory;
  const rejected = images.rejected ?? [];
  const qc = ingest.qc ?? {};
  const timings = ingest.timings_ms ?? {};
  const tiers = ingest.tiers_attempted ?? [];

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Home className="size-4" />
              Listing Ingest
            </CardTitle>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <Badge variant={ingestOutcomeBadgeVariant(ingest.outcome)}>
                {ingest.outcome ?? "unknown"}
              </Badge>
              {ingest.extractor ? (
                <Badge variant="outline">{ingest.extractor}</Badge>
              ) : null}
              {ingest.domain ? (
                <span className="text-sm text-muted-foreground">
                  {ingest.domain}
                </span>
              ) : null}
              {ingest.blocked_reason ? (
                <Badge variant="destructive">{ingest.blocked_reason}</Badge>
              ) : null}
              {ingest.follow_url_used ? (
                <Badge variant="secondary">gallery follow-up</Badge>
              ) : null}
              {ingest.needs_render ? (
                <Badge variant="secondary">needs render</Badge>
              ) : null}
              {ingest.workflow?.template_id ? (
                <Badge variant="outline">
                  workflow {ingest.workflow.template_id}@v
                  {ingest.workflow.version ?? "?"}
                </Badge>
              ) : null}
              {ingest.repair?.attempted ? (
                <Badge variant="secondary">
                  repair {ingest.repair.outcome ?? "attempted"}
                </Badge>
              ) : null}
            </div>
          </div>
          {ingest.listing_url ? (
            <a
              href={ingest.listing_url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
            >
              Open listing
              <ExternalLink className="size-3.5" />
            </a>
          ) : null}
        </div>
        {records.length > 1 ? (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {records.map((row) => (
              <Button
                key={String(row.id)}
                size="sm"
                variant={row.id === selected.id ? "secondary" : "ghost"}
                className="h-7 px-2 text-xs"
                onClick={() => setSelectedId(String(row.id))}
              >
                {row.ingest?.extractor ?? row.ingest?.outcome ?? "attempt"} ·{" "}
                {formatDateTime(row.created_at_source)}
              </Button>
            ))}
          </div>
        ) : null}
      </CardHeader>
      <CardContent className="space-y-4">
        <IngestFieldTable
          ingest={ingest}
          compareWith={showCompare && canCompare ? compareFields : null}
          compareLabel={compareLabel}
        />

        {tiers.length > 0 ? (
          <div className="text-sm text-muted-foreground">
            Extraction ladder:{" "}
            {tiers.map((tier, index) => (
              <span key={`${tier.tier}-${index}`}>
                {index > 0 ? " → " : ""}
                <span
                  className={tier.produced ? "font-medium text-foreground" : ""}
                >
                  {tier.tier} ({formatMs(tier.ms)}){tier.produced ? " ✓" : ""}
                  {tier.error ? " ⚠" : ""}
                </span>
              </span>
            ))}
          </div>
        ) : null}

        <div className="flex flex-wrap items-center gap-2 text-sm">
          <Badge variant="outline">
            <ImageIcon className="size-3" />
            {images.accepted ?? 0} accepted
          </Badge>
          {typeof images.candidates === "number" ? (
            <span className="text-muted-foreground">
              of {images.candidates} candidates ({images.downloaded ?? 0}{" "}
              downloaded)
            </span>
          ) : null}
          {images.transform_applied ? (
            <Badge variant="secondary">full-size variants</Badge>
          ) : null}
          {images.low_res ? (
            <Badge variant="secondary">{images.low_res} low-res</Badge>
          ) : null}
          {images.accepted_hints?.length ? (
            <Badge
              variant="secondary"
              title="Listing alt text / source filename captured per accepted photo (FPR-015); the room map reads them as hints"
            >
              {images.accepted_hints.filter((hint) => hint.alt).length} alt-text
              hints
            </Badge>
          ) : null}
        </div>
        {inventory ? (
          <div className="rounded-md border p-3 text-sm">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-medium">Source completeness</span>
              <Badge
                variant={
                  inventory.completeness === "complete"
                    ? "default"
                    : "secondary"
                }
              >
                {inventory.completeness ?? "unknown"}
              </Badge>
              <span className="text-muted-foreground">
                {inventory.usable_property_photos ?? 0} usable ·{" "}
                {inventory.unique ?? 0} unique
                {inventory.expected_source_count != null
                  ? ` of ${inventory.expected_source_count} advertised`
                  : ""}
              </span>
            </div>
            {inventory.reasons?.length ? (
              <p className="mt-2 text-xs text-muted-foreground">
                {inventory.reasons.join(" · ")}
              </p>
            ) : null}
          </div>
        ) : null}
        {rejected.length > 0 ? (
          <div className="text-xs text-muted-foreground">
            Rejected:{" "}
            {rejected
              .slice(0, 8)
              .map(
                (entry) => `${entry.url_tail ?? "?"} (${entry.reason ?? "?"})`,
              )
              .join(" · ")}
            {rejected.length > 8 ? ` · +${rejected.length - 8} more` : ""}
          </div>
        ) : null}

        <div className="flex flex-wrap items-center gap-2 text-sm">
          {qc.passed === true ? (
            <Badge>QC passed</Badge>
          ) : qc.passed === false ? (
            <Badge variant="destructive">QC failed</Badge>
          ) : null}
          {(qc.failures ?? []).map((failure) => (
            <Badge key={failure} variant="destructive">
              {failure}
            </Badge>
          ))}
          {(qc.warnings ?? []).map((warning) => (
            <Badge key={warning} variant="secondary">
              {warning}
            </Badge>
          ))}
          <span className="ml-auto text-xs text-muted-foreground">
            {["fetch", "extract", "images", "total"]
              .filter((key) => timings[key] !== undefined)
              .map((key) => `${key} ${formatMs(timings[key])}`)
              .join(" · ")}
          </span>
        </div>

        {ingest.floor_plan ? (
          <FloorPlanCard
            plan={ingest.floor_plan}
            bucket={
              ingestMediaBucket(selected, media) ?? getDefaultAwsMediaBucket()
            }
            propertyType={propertyTypeFromRow(session, events)}
          />
        ) : null}

        <div className="flex flex-wrap gap-2">
          {canCompare ? (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setShowCompare((value) => !value)}
            >
              <GitBranch className="size-3.5" />
              {showCompare ? "Hide comparison" : `Compare vs ${compareLabel}`}
            </Button>
          ) : null}
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setShowRaw((value) => !value)}
          >
            <FileJson className="size-3.5" />
            {showRaw ? "Hide raw record" : "Raw record"}
          </Button>
        </div>
        {showCompare && canCompare && ingest.shadow?.diff_fields?.length ? (
          <div className="text-xs text-muted-foreground">
            Shadow disagreement on: {ingest.shadow.diff_fields.join(", ")}
          </div>
        ) : null}
        {showRaw ? (
          <pre className="max-h-80 overflow-auto rounded-md bg-muted p-3 text-xs">
            {JSON.stringify(ingest, null, 2)}
          </pre>
        ) : null}
      </CardContent>
    </Card>
  );
};

// Real-estate sessions surface the establishing map clip and the aerial "dollhouse"
// drone shot as their own playable sections, with the remaining i2v/room/title assets
// grouped under "Scene animations & other". Each clip plays inline (signed on demand by
// MediaPreview -> useMediaUrl -> POST /media/sign). The Map card always renders (its
// absence is worth surfacing); the Drone and Other cards appear only when populated.
const RealEstateAssetSections = ({
  media,
}: {
  media: EchoflicksSessionMedia[];
}) => {
  const mapClips = media.filter((item) => item.asset_role === "map_clip");
  const droneShots = media.filter((item) => item.asset_role === "drone_shot");
  const otherGenerated = media.filter(
    (item) =>
      isGeneratedSceneAsset(item) &&
      item.asset_role !== "map_clip" &&
      item.asset_role !== "drone_shot",
  );

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MapPin className="size-4" />
            Map
          </CardTitle>
        </CardHeader>
        <CardContent>
          {mapClips.length > 0 ? (
            <MediaGrid media={mapClips} />
          ) : (
            <EmptyHint>No map clip was generated for this echo.</EmptyHint>
          )}
        </CardContent>
      </Card>

      {droneShots.length > 0 ? (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Plane className="size-4" />
              Drone / Aerial Shot
            </CardTitle>
          </CardHeader>
          <CardContent>
            <MediaGrid media={droneShots} />
          </CardContent>
        </Card>
      ) : null}

      {otherGenerated.length > 0 ? (
        <Card>
          <CardHeader>
            <CardTitle>Scene animations &amp; other</CardTitle>
          </CardHeader>
          <CardContent>
            <MediaGrid media={otherGenerated} />
          </CardContent>
        </Card>
      ) : null}
    </>
  );
};

const AssetList = ({ media }: { media: EchoflicksSessionMedia[] }) => (
  <div className="space-y-3">
    {media.map((item) => (
      <div key={item.id} className="flex items-start gap-3 text-sm">
        <div className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-md bg-muted">
          <MediaIcon media={item} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="truncate font-medium">
            {item.s3_key?.split("/").at(-1) ?? item.local_path}
          </div>
          <div className="text-muted-foreground">
            {humanizeMediaKind(item.kind)} / {formatBytes(item.size_bytes)}
          </div>
          <MediaAssetLink media={item} label="Open asset" />
        </div>
      </div>
    ))}
  </div>
);

const MessageTimeline = ({
  messages,
  media,
  contactName,
  whatsappId,
}: {
  messages: EchoflicksSessionMessage[];
  media?: EchoflicksSessionMedia[];
  contactName?: string | null;
  whatsappId?: string | null;
}) => {
  const items = useMemo(
    () =>
      buildConversationTimeline(
        messages.map((message) =>
          normalizeSessionMessage(message, contactName),
        ),
        { segmentEchoes: false, mediaItems: media },
      ),
    [messages, media, contactName],
  );

  const name = contactName?.trim() || whatsappId || "Contact";

  return (
    <WhatsAppChat
      items={items}
      heightClassName="h-[460px]"
      showEncryptionNotice={false}
      header={
        <WhatsAppChatHeader name={name} subtitle={whatsappId ?? "WhatsApp"} />
      }
    />
  );
};

const EventList = ({ events }: { events: EchoflicksSessionEvent[] }) => (
  <div className="space-y-3 text-sm">
    {events.map((event) => {
      // A soundtrack event (TRK-240) names the song, its source and track id.
      const detail = sessionEventDetail(event);
      return (
        <div key={event.id}>
          <div className="font-medium">{sessionEventLabel(event)}</div>
          <div className="text-muted-foreground">
            {formatDateTime(event.occurred_at)}
            {detail ? ` · ${detail}` : ""}
          </div>
        </div>
      );
    })}
  </div>
);

const MediaIcon = ({ media }: { media: EchoflicksSessionMedia }) => {
  if (isImage(media.mime_type)) return <ImageIcon className="size-4" />;
  if (isVideo(media.mime_type)) return <Video className="size-4" />;
  if (isAudio(media.mime_type)) return <Music className="size-4" />;
  if (media.kind === "script_json") return <FileJson className="size-4" />;
  return <FileText className="size-4" />;
};

const AssetLink = ({ href, label }: { href: string; label: string }) => (
  <Button asChild variant="link" size="sm" className="h-auto p-0">
    <a href={href} target="_blank" rel="noreferrer">
      {label}
      <ExternalLink className="size-3" />
    </a>
  </Button>
);

const MediaAssetLink = ({
  media,
  label,
}: {
  media: EchoflicksSessionMedia;
  label: string;
}) => {
  const mediaUrl = useMediaUrl(media);

  if (!mediaUrl.url) return null;

  return <AssetLink href={mediaUrl.url} label={label} />;
};
